import { z } from 'zod';
export * from './operations-v2.js';
export const EnvironmentNameSchema = z.enum(['development', 'testing', 'production']);
export const ServiceNameSchema = z.enum(['api', 'worker', 'web']);
export const BuildInformationSchema = z
    .object({
    service: ServiceNameSchema,
    environment: EnvironmentNameSchema,
    buildId: z.string().min(1),
    gitSha: z.string().regex(/^[0-9a-f]{7,64}$/),
})
    .strict();
export const LivenessResponseSchema = z
    .object({
    status: z.literal('ok'),
    service: z.literal('api'),
})
    .strict();
export const DependencyStatusSchema = z
    .object({
    name: z.string().min(1),
    status: z.enum(['ok', 'degraded']),
    reason: z.string().nullable().optional(),
})
    .strict();
export const ReadinessResponseSchema = z
    .object({
    status: z.enum(['ok', 'degraded']),
    dependencies: z.array(DependencyStatusSchema),
})
    .strict();
export const SystemRoleSchema = z.enum([
    'READ_ONLY',
    'ADMIN',
    'ADMINISTRATION',
    'PROJECT_MANAGER',
    'EXECUTOR',
    'BUSINESS',
]);
export const CapabilityBaselineSchema = z.enum([
    'READ_ONLY',
    'EXECUTOR',
    'CONFIRMER',
    'PLANNER',
    'MANAGER',
    'FINANCE',
    'SYSTEM_ADMIN',
    // BUG-0184：发布专员是独立身份，不复用执行人/策划；能力=只读 + publication.record。
    'PUBLISHER',
]);
export const PageKeySchema = z.enum([
    'WORKSPACE',
    'MANAGEMENT_DASHBOARD',
    'PROJECTS',
    'CONTRACTS',
    'SETTLEMENTS',
    'PEOPLE',
    'CUSTOMERS',
    'SERVICE_TEMPLATES',
    'SYSTEM',
    'PROJECT_PACKAGES',
    'PROJECT_CONTENT',
    'PROJECT_DELIVERIES',
    'PROJECT_PUBLICATIONS',
    'PROJECT_WORK_EXPENSES',
    'PROJECT_CHANGES',
    'PROJECT_FINANCE',
    'PROJECT_CONTRACTS',
    'PROJECT_MEMBERS',
    'PROJECT_SETTINGS',
]);
export const CapabilityCodeSchema = z.enum([
    'workspace.read',
    'project.read',
    'project.create',
    'project.update',
    'project.lifecycle',
    'person.read',
    'person.manage',
    'person.allocation_status',
    'customer.read',
    'customer.manage',
    'template.read',
    'template.manage',
    'template.publish',
    'contract.read',
    'contract.manage',
    'account.manage',
    'role.manage',
    'member.read',
    'member.manage',
    'package.read',
    'package.manage_draft',
    'package.submit',
    'package.confirm',
    'package.return',
    'package.start',
    'package.complete',
    'content.create',
    'content.correct_variant',
    'content.issue_resolve',
    'task.assign',
    'task.accept',
    'task.progress',
    'task.void',
    'material.manage',
    'delivery.register',
    'delivery.change',
    'publication.plan',
    'publication.record',
    'work.submit',
    'work.review',
    'reimbursement.submit',
    'reimbursement.review',
    'reimbursement.pay',
    'finance.project_read',
    'finance.adjustment',
    'contract.link',
    'settlement.pending_read',
    'settlement.generate',
    'settlement.void',
    'settlement.pay',
    'settlement.export',
]);
export const SessionRoleSchema = z.object({
    roleId: z.uuid(),
    code: z.string().regex(/^[A-Z][A-Z0-9_]{2,63}$/),
    name: z.string().min(1).max(120),
    capabilityBaseline: CapabilityBaselineSchema,
}).strict();
export const SessionResponseSchema = z
    .object({
    userId: z.uuid(),
    personId: z.uuid(),
    username: z.string().regex(/^[a-z0-9][a-z0-9._-]{2,63}$/),
    displayName: z.string().min(1).max(120),
    roles: z.array(SystemRoleSchema),
    roleAssignments: z.array(SessionRoleSchema),
    pageAccess: z.array(PageKeySchema),
    capabilities: z.array(CapabilityCodeSchema),
    client: z.enum(['web', 'mcp']),
})
    .strict();
export const RoleStatusSchema = z.enum(['ACTIVE', 'INACTIVE']);
export const AccountStatusSchema = z.enum(['ACTIVE', 'SUSPENDED']);
export const DynamicRoleSchema = z.object({
    roleId: z.uuid(),
    code: z.string().regex(/^[A-Z][A-Z0-9_]{2,63}$/),
    name: z.string().min(1).max(120),
    description: z.string().min(1).max(1000).nullable(),
    kind: z.enum(['BUILT_IN', 'CUSTOM']),
    status: RoleStatusSchema,
    capabilityBaseline: CapabilityBaselineSchema,
    pageKeys: z.array(PageKeySchema),
    assignedAccountCount: z.number().int().nonnegative(),
    protected: z.boolean(),
    version: z.number().int().positive(),
}).strict();
export const DynamicRoleListSchema = z.object({ items: z.array(DynamicRoleSchema) }).strict();
export const AccountSummarySchema = z.object({
    accountId: z.uuid(),
    personId: z.uuid(),
    personCode: z.string().min(1).max(120),
    displayName: z.string().min(1).max(120),
    username: z.string().regex(/^[a-z0-9][a-z0-9._-]{2,63}$/),
    status: AccountStatusSchema,
    note: z.string().min(1).max(1000).nullable(),
    identityLinked: z.boolean(),
    lastLoginAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    roles: z.array(SessionRoleSchema),
    version: z.number().int().positive(),
}).strict();
export const AccountSummaryListSchema = z.object({ items: z.array(AccountSummarySchema) }).strict();
export const CentralIdentityCandidateSchema = z.object({
    centralUserId: z.uuid(),
    displayName: z.string().min(1).max(200),
    kind: z.enum(['INTERNAL', 'EXTERNAL']),
    email: z.email().nullable(),
    departmentName: z.string().min(1).max(120).nullable(),
    directoryStatus: z.string().min(1).max(40),
    boundPersonId: z.uuid().nullable(),
    boundPersonCode: z.string().min(1).max(120).nullable(),
    boundAccountId: z.uuid().nullable(),
    boundAccountUsername: z.string().regex(/^[a-z0-9][a-z0-9._-]{2,63}$/).nullable(),
    suggestedAccountIds: z.array(z.uuid()),
}).strict();
export const CentralIdentityCandidateListSchema = z.object({
    items: z.array(CentralIdentityCandidateSchema),
}).strict();
export const ExternalInvitationStatusSchema = z.enum([
    'PENDING_DELIVERY', 'PENDING_ACCEPTANCE', 'ACCEPTED', 'RECONCILED', 'CANCELLED', 'EXPIRED', 'FAILED',
]);
export const ExternalInvitationTargetModeSchema = z.enum(['CLAIM_EXISTING', 'CREATE_NEW']);
export const ExternalInvitationIdentityPathSchema = z.enum(['EXISTING_USER', 'EXISTING_AD', 'NEW_USER']);
export const IdentityBindingStateSchema = z.enum(['UNBOUND', 'VERIFIED', 'CONFLICT']);
export const ExternalInvitationBusinessStatusSchema = z.enum(['PENDING', 'ACCEPTED', 'CANCELLED', 'EXPIRED']);
export const ExternalInvitationProcessingStatusSchema = z.enum([
    'RESERVED', 'WAITING_IDENTITY', 'RETRY_PENDING', 'RECONCILING', 'PROJECTING', 'COMPLETED', 'FAILED', 'CANCELLED',
]);
export const ExternalInvitationSchema = z.object({
    invitationId: z.uuid(),
    centralInvitationId: z.uuid().nullable(),
    email: z.email(),
    displayName: z.string().min(1).max(120),
    sponsorCentralUserId: z.uuid(),
    targetMode: ExternalInvitationTargetModeSchema,
    targetPersonId: z.uuid().nullable(),
    targetPersonCode: z.string().min(1).max(120).nullable(),
    targetPersonName: z.string().min(1).max(120).nullable(),
    identityPath: ExternalInvitationIdentityPathSchema,
    identityUserId: z.uuid().nullable(),
    adUsername: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,19}$/).nullable(),
    adUidNumber: z.number().int().positive().nullable(),
    roleId: z.uuid(),
    roleName: z.string().min(1).max(120),
    status: ExternalInvitationStatusSchema,
    businessStatus: ExternalInvitationBusinessStatusSchema,
    processingStatus: ExternalInvitationProcessingStatusSchema,
    deliveryStatus: z.enum(['NOT_CONFIGURED', 'PENDING', 'SENT', 'FAILED', 'BOUNCED']).nullable(),
    deliveryError: z.string().max(1000).nullable(),
    inviteExpiresAt: z.iso.datetime().nullable(),
    lastDeliveredAt: z.iso.datetime().nullable(),
    accountExpiresAt: z.iso.datetime(),
    acceptedCentralUserId: z.uuid().nullable(),
    reconciledAccountId: z.uuid().nullable(),
    reason: z.string().min(2).max(500),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    version: z.number().int().positive(),
}).strict();
export const ExternalInvitationListSchema = z.object({ items: z.array(ExternalInvitationSchema) }).strict();
export const ExternalPersonCandidateSchema = z.object({
    personId: z.uuid(), personCode: z.string().min(1), displayName: z.string().min(1),
    company: z.string().nullable(), email: z.email().nullable(), centralUserId: z.uuid().nullable(),
    adUsername: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,19}$/).nullable(),
    adUidNumber: z.number().int().positive().nullable(), bindingState: IdentityBindingStateSchema,
    identityPath: ExternalInvitationIdentityPathSchema.nullable(),
    accountId: z.uuid().nullable(), accountStatus: AccountStatusSchema.nullable(),
    projectCount: z.number().int().nonnegative(), taskCount: z.number().int().nonnegative(),
    hasFinancialHistory: z.boolean(), canClaim: z.boolean(), blockReason: z.string().nullable(),
    version: z.number().int().positive(),
}).strict();
export const ExternalPersonCandidateListSchema = z.object({ items: z.array(ExternalPersonCandidateSchema) }).strict();
export const ExternalIdentityMappingSyncResponseSchema = z.object({
    received: z.number().int().nonnegative(),
    matched: z.number().int().nonnegative(),
    verified: z.number().int().nonnegative(),
    unchanged: z.number().int().nonnegative(),
    conflicts: z.number().int().nonnegative(),
    unmatched: z.number().int().nonnegative(),
}).strict();
export const CreateExternalInvitationRequestSchema = z.object({
    email: z.email().max(254), displayName: z.string().trim().min(2).max(120), sponsorCentralUserId: z.uuid(),
    targetMode: ExternalInvitationTargetModeSchema, targetPersonId: z.uuid().nullable(),
    expectedPersonVersion: z.number().int().positive().nullable(), roleId: z.uuid(),
    accountExpiresAt: z.iso.datetime({ offset: true }), reason: z.string().trim().min(3).max(500),
}).strict().superRefine((value, context) => {
    if ((value.targetMode === 'CLAIM_EXISTING') !== Boolean(value.targetPersonId && value.expectedPersonVersion)) {
        context.addIssue({ code: 'custom', message: '历史人员模式必须提供人员和版本' });
    }
    if (value.targetMode === 'CREATE_NEW' && (value.targetPersonId || value.expectedPersonVersion)) {
        context.addIssue({ code: 'custom', message: '新建人员模式不能指定历史人员' });
    }
});
export const ExternalInvitationWriteResponseSchema = z.object({
    invitation: ExternalInvitationSchema, inviteUrl: z.url().nullable(), replayed: z.boolean(),
}).strict();
export const ExternalInvitationRecoveryResponseSchema = z.object({
    invitation: ExternalInvitationSchema, inviteUrl: z.url(),
}).strict();
export const EngagementTypeSchema = z.enum(['EMPLOYEE', 'LONG_TERM_EXECUTOR']);
export const AllocationStatusSchema = z.enum(['ACTIVE', 'ALLOCATION_PAUSED']);
export const PersonProjectRelationSchema = z.object({
    projectId: z.uuid(),
    projectCode: z.string().min(1).max(120),
    projectName: z.string().min(1).max(200),
    projectStatus: z.string().min(1).max(40),
    roles: z.array(z.string().min(1).max(80)),
}).strict();
export const PersonProfileSchema = z.object({
    personId: z.uuid(),
    personCode: z.string().min(1).max(120),
    displayName: z.string().min(1).max(120),
    engagementType: EngagementTypeSchema,
    company: z.string().min(1).max(160).nullable(),
    department: z.string().min(1).max(120).nullable(),
    jobTitle: z.string().min(1).max(120).nullable(),
    phone: z.string().min(1).max(40).nullable(),
    email: z.email().max(254).nullable(),
    skills: z.array(z.string().min(1).max(60)),
    allocationStatus: AllocationStatusSchema,
    active: z.boolean(),
    nameLocked: z.boolean(),
    account: z.object({
        accountId: z.uuid(),
        username: z.string().regex(/^[a-z0-9][a-z0-9._-]{2,63}$/),
        status: AccountStatusSchema,
    }).strict().nullable(),
    activeProjectCount: z.number().int().nonnegative(),
    activeTaskCount: z.number().int().nonnegative(),
    completedTaskCount: z.number().int().nonnegative(),
    projects: z.array(PersonProjectRelationSchema),
    version: z.number().int().positive(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
}).strict();
export const PersonProfileListSchema = z.object({ items: z.array(PersonProfileSchema) }).strict();
const RoleCodeSchema = z.string().trim().toUpperCase().regex(/^[A-Z][A-Z0-9_]{2,63}$/);
const RoleNameSchema = z.string().trim().min(1).max(120);
const OptionalDescriptionSchema = z.string().trim().min(1).max(1000).nullable().optional();
const UniquePageKeysSchema = z.array(PageKeySchema).min(1)
    .refine((keys) => new Set(keys).size === keys.length, 'pageKeys must be unique');
const UniqueRoleIdsSchema = z.array(z.uuid()).min(1)
    .refine((ids) => new Set(ids).size === ids.length, 'roleIds must be unique');
const ChangeReasonSchema = z.string().trim().min(2).max(500);
const AccountUsernameSchema = z.string().trim().toLowerCase().regex(/^[a-z0-9][a-z0-9._-]{2,63}$/);
const OptionalAccountNoteSchema = z.string().trim().min(1).max(1000).nullable().optional();
const PersonNameSchema = z.string().trim().min(1).max(120);
const OptionalPersonTextSchema = (maximum) => z.string().trim().min(1).max(maximum).nullable().optional();
const PersonSkillsSchema = z.array(z.string().trim().min(1).max(60)).max(30)
    .refine((skills) => new Set(skills.map((skill) => skill.toLocaleLowerCase())).size === skills.length, 'skills must be unique');
export const CreatePersonRequestSchema = z.object({
    displayName: PersonNameSchema,
    engagementType: EngagementTypeSchema,
    company: OptionalPersonTextSchema(160),
    department: OptionalPersonTextSchema(120),
    jobTitle: OptionalPersonTextSchema(120),
    phone: OptionalPersonTextSchema(40),
    email: z.union([z.email().max(254), z.null()]).optional(),
    skills: PersonSkillsSchema,
    allocationStatus: AllocationStatusSchema,
    reason: ChangeReasonSchema,
}).strict();
export const UpdatePersonRequestSchema = z.object({
    expectedVersion: z.number().int().positive(),
    displayName: PersonNameSchema,
    engagementType: EngagementTypeSchema,
    company: OptionalPersonTextSchema(160),
    department: OptionalPersonTextSchema(120),
    jobTitle: OptionalPersonTextSchema(120),
    phone: OptionalPersonTextSchema(40),
    email: z.union([z.email().max(254), z.null()]).optional(),
    skills: PersonSkillsSchema,
    reason: ChangeReasonSchema,
}).strict();
export const ChangePersonAllocationStatusRequestSchema = z.object({
    expectedVersion: z.number().int().positive(),
    allocationStatus: AllocationStatusSchema,
    reason: ChangeReasonSchema,
}).strict();
export const PersonWriteResponseSchema = PersonProfileSchema.extend({ replayed: z.boolean() }).strict();
export const CreateAccountRequestSchema = z.object({
    personId: z.uuid(),
    username: AccountUsernameSchema,
    roleIds: UniqueRoleIdsSchema,
    note: OptionalAccountNoteSchema,
    reason: ChangeReasonSchema,
}).strict();
export const UpdateAccountRequestSchema = z.object({
    expectedVersion: z.number().int().positive(),
    username: AccountUsernameSchema,
    note: OptionalAccountNoteSchema,
    reason: ChangeReasonSchema,
}).strict();
export const CreateDynamicRoleRequestSchema = z.object({
    code: RoleCodeSchema,
    name: RoleNameSchema,
    description: OptionalDescriptionSchema,
    capabilityBaseline: CapabilityBaselineSchema.exclude(['SYSTEM_ADMIN']),
    pageKeys: UniquePageKeysSchema,
    reason: ChangeReasonSchema,
}).strict();
export const CloneDynamicRoleRequestSchema = z.object({
    code: RoleCodeSchema,
    name: RoleNameSchema,
    reason: ChangeReasonSchema,
}).strict();
export const UpdateDynamicRoleRequestSchema = z.object({
    expectedVersion: z.number().int().positive(),
    name: RoleNameSchema,
    description: OptionalDescriptionSchema,
    capabilityBaseline: CapabilityBaselineSchema.exclude(['SYSTEM_ADMIN']),
    reason: ChangeReasonSchema,
}).strict();
export const ChangeRoleStatusRequestSchema = z.object({
    expectedVersion: z.number().int().positive(),
    status: RoleStatusSchema,
    reason: ChangeReasonSchema,
}).strict();
export const ReplaceRolePagesRequestSchema = z.object({
    expectedVersion: z.number().int().positive(),
    pageKeys: UniquePageKeysSchema,
    reason: ChangeReasonSchema,
}).strict();
export const ReplaceAccountRolesRequestSchema = z.object({
    expectedVersion: z.number().int().positive(),
    roleIds: UniqueRoleIdsSchema,
    reason: ChangeReasonSchema,
}).strict();
export const ChangeAccountStatusRequestSchema = z.object({
    expectedVersion: z.number().int().positive(),
    status: AccountStatusSchema,
    reason: ChangeReasonSchema,
}).strict();
export const CompleteIdentityBindingRequestSchema = z.object({
    reason: ChangeReasonSchema,
}).strict();
export const BindCentralIdentityRequestSchema = z.object({
    centralUserId: z.uuid(),
    expectedVersion: z.number().int().positive(),
    reason: ChangeReasonSchema,
}).strict();
export const DynamicRoleWriteResponseSchema = DynamicRoleSchema.extend({ replayed: z.boolean() }).strict();
export const AccountWriteResponseSchema = AccountSummarySchema.extend({ replayed: z.boolean() }).strict();
export const IdentityBindingResponseSchema = z.object({
    account: AccountSummarySchema,
    temporaryPassword: z.string().min(1).nullable(),
    replayed: z.boolean(),
}).strict();
export const AccessCatalogSchema = z.object({
    pages: z.array(z.object({ key: PageKeySchema, label: z.string().min(1) }).strict()),
    capabilityBaselines: z.array(z.object({
        code: CapabilityBaselineSchema,
        label: z.string().min(1),
        description: z.string().min(1),
        capabilities: z.array(z.object({
            code: CapabilityCodeSchema,
            label: z.string().min(1),
            group: z.string().min(1),
        }).strict()),
    }).strict()),
}).strict();
export const MyPayeeSchema = z.object({
    payeeId: z.uuid(),
    personId: z.uuid(),
    personCode: z.string().min(1),
    displayName: z.string().min(1),
    engagementType: z.enum(['EMPLOYEE', 'LONG_TERM_EXECUTOR']),
    settlementCycle: z.enum(['WEEKLY', 'MONTHLY']),
    status: z.enum(['ACTIVE', 'INACTIVE']),
}).strict();
// 应计明细行：本人结算明细与行政结算预览共用同一形状，避免两处各自演化。
export const AccrualLineSchema = z.object({
    accrualId: z.uuid(),
    businessOn: z.iso.date(),
    projectId: z.uuid(),
    projectName: z.string().min(1),
    packageId: z.uuid(),
    packageCode: z.string().min(1),
    contentId: z.uuid(),
    contentCode: z.string().min(1),
    taskId: z.uuid(),
    taskCode: z.string().min(1),
    shareBasisPoints: z.number().int().positive(),
    amount: z.string(),
    // 该行是否已进入某张正式结算单。已结算的行不再参与下一次确认。
    settled: z.boolean(),
}).strict();
// 本人结算明细：只返回当前账号自己收款对象在某一结算周期内的应计行。
// 不接受调用方传入人员或收款对象标识；金额与范围由服务端按会话解析。
export const MyAccrualStatementSchema = z.object({
    payeeId: z.uuid(),
    settlementCycle: z.enum(['WEEKLY', 'MONTHLY']),
    periodFrom: z.iso.date(),
    periodUntil: z.iso.date(),
    itemCount: z.number().int().nonnegative(),
    totalAmount: z.string(),
    // 明细行超过服务端单次返回上限时为 true；合计与行数始终是全量口径。
    truncated: z.boolean(),
    items: z.array(AccrualLineSchema),
}).strict();
// 行政结算预览：按结算周期类型汇总全部收款人，用于与手算结果逐人对账。
// 只返回该周期类型对应用工类型的人员（周结=长期合作执行人员，月结=全职员工）。
export const SettlementPersonSchema = z.object({
    payeeId: z.uuid(),
    personId: z.uuid(),
    personCode: z.string().min(1),
    displayName: z.string().min(1),
    engagementType: z.enum(['EMPLOYEE', 'LONG_TERM_EXECUTOR']),
    lineCount: z.number().int().nonnegative(),
    totalAmount: z.string(),
    // 未结算口径：确认结算单时实际会锁定的部分。
    unsettledLineCount: z.number().int().nonnegative(),
    unsettledAmount: z.string(),
    lines: z.array(AccrualLineSchema),
}).strict();
export const SettlementPreviewSchema = z.object({
    cycle: z.enum(['WEEKLY', 'MONTHLY']),
    periodFrom: z.iso.date(),
    periodUntil: z.iso.date(),
    personCount: z.number().int().nonnegative(),
    lineCount: z.number().int().nonnegative(),
    totalAmount: z.string(),
    // 全量口径与未结算口径并列：对账看全量，确认结算单只看未结算。
    unsettledPersonCount: z.number().int().nonnegative(),
    unsettledLineCount: z.number().int().nonnegative(),
    unsettledAmount: z.string(),
    // 明细行超出单次返回上限时为 true；合计、人数与行数始终是全量口径。
    truncated: z.boolean(),
    people: z.array(SettlementPersonSchema),
}).strict();
// 正式结算单：行政确认某一周期后生成，锁定该周期尚未结算的全部应计。
// 确认后不可改写；更正与冲销以新记录表达，已付款的负向差额只标记线下处理。
export const ConfirmedSettlementStatementSchema = z.object({
    statementId: z.uuid(),
    statementCode: z.string().min(1),
    cycle: z.enum(['WEEKLY', 'MONTHLY']),
    periodFrom: z.iso.date(),
    periodUntil: z.iso.date(),
    status: z.literal('CONFIRMED'),
    payeeCount: z.number().int().positive(),
    lineCount: z.number().int().positive(),
    totalAmount: z.string(),
    confirmedBy: z.uuid(),
    confirmedAt: z.iso.datetime(),
    replayed: z.boolean().optional(),
}).strict();
export const SettlementStatementListSchema = z.object({
    items: z.array(ConfirmedSettlementStatementSchema),
}).strict();
export const ConfirmSettlementStatementRequestSchema = z.object({
    cycle: z.enum(['WEEKLY', 'MONTHLY']),
    // 缺省取上海时区当天；服务端再校验为合法日历日期。
    on: z.iso.date().optional(),
}).strict();
// 结算单详情：行政据以登记付款——该付谁、锁了多少、已付多少、还剩多少。
export const SettlementStatementPayeeSchema = z.object({
    payeeId: z.uuid(),
    personId: z.uuid(),
    personCode: z.string().min(1),
    displayName: z.string().min(1),
    lineCount: z.number().int().positive(),
    lockedAmount: z.string(),
    paidAmount: z.string(),
    outstandingAmount: z.string(),
}).strict();
export const SettlementStatementDetailSchema = z.object({
    statement: ConfirmedSettlementStatementSchema,
    payees: z.array(SettlementStatementPayeeSchema),
}).strict();
// V1 #10 项目级成本与应计汇总。负责人看自己项目的钱。
//
// 金额口径刻意分成四个互不重叠的桶，避免"已花多少"这类含糊问法：
//   budget          包批准总预算
//   fixedCommitment 启动时按档位×任务锁定的人员费用承诺（不应超过 budget）
//   accrued         已交付确认并生成的人员应计（未冲销部分）
//   settled         已进入正式结算单的应计（是 accrued 的子集，不是另加）
//   paid            已登记线下付款的金额（是 settled 的子集）
// 因此 accrued 是"已发生的人员费用"，budget - accrued 才是"尚未发生"。
export const ProjectCostPackageSchema = z.object({
    packageId: z.uuid(),
    packageCode: z.string().min(1),
    contentType: z.enum(['VIDEO', 'ARTICLE']),
    status: z.string().min(1),
    budget: z.string(),
    fixedCommitment: z.string(),
    flexibleReserve: z.string(),
    capacity: z.number().int().positive(),
    accruedAmount: z.string(),
    settledAmount: z.string(),
    paidAmount: z.string(),
    /** 已冲销金额（正数表示被冲掉多少），用于解释 accrued 与固定承诺的差异。 */
    reversedAmount: z.string(),
    accrualLineCount: z.number().int().nonnegative(),
}).strict();
export const ProjectCostPersonSchema = z.object({
    payeeId: z.uuid(),
    personId: z.uuid(),
    personCode: z.string().min(1),
    displayName: z.string().min(1),
    accrualLineCount: z.number().int().positive(),
    accruedAmount: z.string(),
    settledAmount: z.string(),
    paidAmount: z.string(),
}).strict();
export const ProjectCostSummarySchema = z.object({
    projectId: z.uuid(),
    projectCode: z.string().min(1),
    projectName: z.string().min(1),
    status: z.string().min(1),
    /** 汇总口径与 packages 逐包求和一致，便于前端核对。 */
    budget: z.string(),
    fixedCommitment: z.string(),
    accruedAmount: z.string(),
    settledAmount: z.string(),
    paidAmount: z.string(),
    reversedAmount: z.string(),
    packages: z.array(ProjectCostPackageSchema),
    people: z.array(ProjectCostPersonSchema),
}).strict();
// 线下实际付款的登记。系统只登记事实，不发起付款；已登记记录不可改写，
// 纠错需以冲销记录表达（阶段 2c）。
export const SettlementPaymentMethodSchema = z.enum(['BANK_TRANSFER', 'CASH', 'OTHER']);
export const RecordSettlementPaymentRequestSchema = z.object({
    statementId: z.uuid(),
    payeeId: z.uuid(),
    paidOn: z.iso.date(),
    amount: z.string(),
    method: SettlementPaymentMethodSchema,
    reference: z.string().trim().min(1).max(120).optional(),
}).strict();
export const SettlementPaymentReceiptSchema = z.object({
    paymentId: z.uuid(),
    statementId: z.uuid(),
    statementCode: z.string().min(1),
    payeeId: z.uuid(),
    personId: z.uuid(),
    personCode: z.string().min(1),
    displayName: z.string().min(1),
    paidOn: z.iso.date(),
    amount: z.string(),
    method: SettlementPaymentMethodSchema,
    reference: z.string().nullable(),
    // 该收款人在该结算单上的锁定、累计已付与剩余未付，便于前端直接展示。
    lockedAmount: z.string(),
    paidAmount: z.string(),
    outstandingAmount: z.string(),
    recordedBy: z.uuid(),
    recordedAt: z.iso.datetime(),
    replayed: z.boolean().optional(),
}).strict();
export const SettlementPaymentListSchema = z.object({
    items: z.array(SettlementPaymentReceiptSchema),
}).strict();
// V1 #9 冲销与更正。
// 应计行只追加，因此冲销是一条独立的负向记录，原应计保持不变。
export const AccrualReversalOutcomeSchema = z.enum(['REVERSED', 'OFFLINE_ONLY']);
export const ReverseAccrualRequestSchema = z.object({
    reason: z.string().trim().min(4).max(500),
    businessOn: z.iso.date(),
}).strict();
export const AccrualReversalReceiptSchema = z.object({
    reversalId: z.uuid(),
    /** REVERSED：未付款，已在系统内冲销；OFFLINE_ONLY：已付款，只登记线下处理。 */
    outcome: AccrualReversalOutcomeSchema,
    accrualId: z.uuid(),
    projectId: z.uuid(),
    packageId: z.uuid(),
    contentId: z.uuid(),
    taskId: z.uuid(),
    payeeId: z.uuid(),
    personId: z.uuid(),
    personCode: z.string().min(1),
    displayName: z.string().min(1),
    /** 原应计金额（正数），保留原始事实。 */
    originalAmount: z.string(),
    /** 本次冲销金额：REVERSED 为原金额的负值；OFFLINE_ONLY 为 "0.00"。 */
    reversalAmount: z.string(),
    /** 已付款金额，供财务线下处理参考。 */
    paidAmount: z.string(),
    reason: z.string().min(1),
    businessOn: z.iso.date(),
    reversedBy: z.uuid(),
    reversedAt: z.iso.datetime(),
    replayed: z.boolean().optional(),
}).strict();
const BankAccountBaseSchema = z.object({
    payeeId: z.uuid(), personId: z.uuid(), personCode: z.string().min(1), displayName: z.string().min(1),
    version: z.number().int().positive(), maskedAccountNumber: z.string().regex(/^•{4} [0-9A-Z]{4}$/),
    updatedAt: z.iso.datetime(),
});
export const BankAccountSchema = z.discriminatedUnion('visibility', [
    BankAccountBaseSchema.extend({ visibility: z.literal('FULL'), accountHolder: z.string().min(1),
        bankName: z.string().min(1), accountNumber: z.string().regex(/^[0-9A-Z]{8,34}$/),
        bankLocation: z.string().min(1).max(160).nullable(), paymentNote: z.string().min(1).max(500).nullable() }).strict(),
    BankAccountBaseSchema.extend({ visibility: z.literal('MASKED'), accountHolder: z.null(),
        bankName: z.null(), accountNumber: z.null(), bankLocation: z.null(), paymentNote: z.null() }).strict(),
]);
export const BankAccountDirectoryEntrySchema = z.object({
    payeeId: z.uuid(), personId: z.uuid(), personCode: z.string().min(1), displayName: z.string().min(1),
    hasAccount: z.boolean(), version: z.number().int().nonnegative(),
    maskedAccountNumber: z.string().regex(/^•{4} [0-9A-Z]{4}$/).nullable(),
    updatedAt: z.iso.datetime().nullable(),
}).strict().superRefine((value, context) => {
    if (value.hasAccount !== (value.version > 0 && value.maskedAccountNumber !== null && value.updatedAt !== null)) {
        context.addIssue({ code: 'custom', message: 'Bank account directory state is inconsistent' });
    }
});
export const BankAccountListSchema = z.object({ items: z.array(BankAccountDirectoryEntrySchema),
    nextCursor: z.string().nullable() }).strict();
export const SaveBankAccountRequestSchema = z.object({ expectedVersion: z.number().int().nonnegative(),
    accountHolder: z.string().trim().min(1).max(120), bankName: z.string().trim().min(1).max(160),
    accountNumber: z.string().trim().min(8).max(64), bankLocation: z.string().trim().min(1).max(160).nullable(),
    paymentNote: z.string().trim().min(1).max(500).nullable(), reason: z.string().trim().min(10).max(500) }).strict();
export const SaveBankAccountResponseSchema = z.object({ payeeId: z.uuid(), version: z.number().int().positive(),
    maskedAccountNumber: z.string().regex(/^•{4} [0-9A-Z]{4}$/), updatedAt: z.iso.datetime(),
    replayed: z.boolean() }).strict();
export const BankAccountExportRequestSchema = z.object({ purpose: z.string().trim().min(10).max(500) }).strict();
export const BankAccountExportTicketSchema = z.object({ ticketId: z.uuid(), expiresAt: z.iso.datetime(),
    rowCount: z.literal(1), fileHash: z.string().regex(/^[0-9a-f]{64}$/) }).strict();
export const DeadLetterSummarySchema = z.object({
    messageId: z.uuid(), topic: z.string(), aggregateType: z.string(), aggregateId: z.uuid(),
    attemptCount: z.number().int().nonnegative(), replayGeneration: z.number().int().nonnegative(),
    failureCode: z.string().nullable(), deadLetteredAt: z.iso.datetime(),
    replayable: z.boolean(), impactCount: z.literal(1), amount: z.null(),
}).strict();
export const DeadLetterListSchema = z.object({ items: z.array(DeadLetterSummarySchema),
    nextCursor: z.uuid().nullable() }).strict();
export const ReplayOutboxResponseSchema = z.object({ messageId: z.uuid(), replayGeneration: z.number().int().positive(),
    status: z.literal('QUEUED'), replayed: z.boolean() }).strict();
export const WebhookDeadLetterSummarySchema = z.object({
    deliveryId: z.uuid(), messageId: z.uuid(), consumerName: z.string(), topic: z.string(),
    aggregateType: z.string(), aggregateId: z.uuid(), attemptCount: z.number().int().nonnegative(),
    replayGeneration: z.number().int().nonnegative(), failureCode: z.string().nullable(),
    deadLetteredAt: z.iso.datetime(), impactCount: z.literal(1), amount: z.null(),
}).strict();
export const WebhookDeadLetterListSchema = z.object({ items: z.array(WebhookDeadLetterSummarySchema),
    nextCursor: z.uuid().nullable() }).strict();
export const ReplayWebhookResponseSchema = z.object({ deliveryId: z.uuid(),
    replayGeneration: z.number().int().positive(), status: z.literal('QUEUED'), replayed: z.boolean() }).strict();
export const ExportGrantCategorySchema = z.enum(['CONTRACT_AMOUNT', 'BANK_ACCOUNT']);
export const ExportGrantChangeActionSchema = z.enum(['GRANT', 'REVOKE']);
export const ExportGrantAccountSchema = z.object({
    userId: z.uuid(), personId: z.uuid(), personCode: z.string().min(1), displayName: z.string().min(1),
    engagementType: z.enum(['EMPLOYEE', 'LONG_TERM_EXECUTOR']), granted: z.boolean(),
    grantEffectiveAt: z.iso.datetime().nullable(),
}).strict();
export const ExportGrantAccountListSchema = z.object({ items: z.array(ExportGrantAccountSchema),
    nextCursor: z.string().nullable() }).strict();
export const ExportGrantPreviewRequestSchema = z.object({ targetUserId: z.uuid(), category: ExportGrantCategorySchema,
    action: ExportGrantChangeActionSchema, reason: z.string().trim().min(10).max(500) }).strict();
export const ExportGrantPreviewSchema = z.object({ previewId: z.uuid(), target: ExportGrantAccountSchema,
    category: ExportGrantCategorySchema, action: ExportGrantChangeActionSchema,
    currentGranted: z.boolean(), targetGranted: z.boolean(), reason: z.string().min(10).max(500),
    impact: z.object({ users: z.literal(1), permissions: z.literal(1),
        activeTickets: z.number().int().nonnegative(), amount: z.null() }).strict(),
    sourceDigest: z.string().regex(/^[0-9a-f]{64}$/), expiresAt: z.iso.datetime(), confirmPath: z.string().min(1),
}).strict();
export const ExportGrantChangeResponseSchema = z.object({ targetUserId: z.uuid(),
    category: ExportGrantCategorySchema, granted: z.boolean(), grantId: z.uuid(), changedAt: z.iso.datetime(),
    replayed: z.boolean() }).strict();
export const WebOidcConfigSchema = z.object({
    issuer: z.url(), clientId: z.string().min(1), allowInsecureHttpAuth: z.boolean(),
}).strict();
export const CreateProjectRequestSchema = z
    .object({
    expectedVersion: z.literal(0),
    businessEntityId: z.uuid(),
    name: z.string().trim().min(1).max(120),
    primaryManagerId: z.uuid(),
})
    .strict();
export const CreateProjectResponseSchema = z
    .object({
    projectId: z.uuid(),
    projectCode: z.string().regex(/^PRJ-[0-9]{4}-[0-9]{6}$/),
    version: z.literal(1),
    status: z.literal('PREPARING'),
    replayed: z.boolean(),
})
    .strict();
export const ProjectStatusSchema = z.enum(['PREPARING', 'ACTIVE', 'SUSPENDED', 'ARCHIVED']);
export const ProjectSummarySchema = z
    .object({
    projectId: z.uuid(),
    projectCode: z.string().regex(/^PRJ-[0-9]{4}-[0-9]{6}$/),
    name: z.string().min(1).max(120),
    status: ProjectStatusSchema,
    version: z.number().int().positive(),
    description: z.string().max(2000).nullable(),
    suspendedReason: z.string().max(500).nullable(),
    statusBeforeSuspend: z.enum(['PREPARING', 'ACTIVE']).nullable(),
    memberCount: z.number().int().nonnegative(),
    contractCount: z.number().int().nonnegative(),
    servicePackageCount: z.number().int().nonnegative(),
    businessEntity: z
        .object({
        id: z.uuid(),
        code: z.string().min(1),
        name: z.string().min(1),
    })
        .strict(),
    primaryManager: z
        .object({
        id: z.uuid(),
        code: z.string().min(1),
        displayName: z.string().min(1),
    })
        .strict(),
})
    .strict();
export const ProjectListResponseSchema = z
    .object({
    items: z.array(ProjectSummarySchema),
    nextCursor: z.string().regex(/^PRJ-[0-9]{4}-[0-9]{6}$/).nullable(),
})
    .strict();
export const ProjectCreationOptionsResponseSchema = z.object({
    items: z.array(z.object({ id: z.uuid(), code: z.string().min(1), name: z.string().min(1) }).strict()),
    nextCursor: z.string().nullable(),
}).strict();
export const ProjectDutySchema = z.enum([
    'PROJECT_MANAGER', 'PACKAGE_MANAGER', 'START_CONFIRMER', 'DELIVERY_CONFIRMER',
    'TASK_EXECUTOR', 'CONTENT_PLANNER', 'VIDEO_EDITOR', 'VIDEO_PRODUCTION', 'FINANCE_VIEWER',
    'PUBLICATION_OPERATOR',
]);
export const ProjectAccessProfileSchema = z.object({
    projectId: z.uuid(),
    membershipId: z.uuid().nullable(),
    duties: z.array(ProjectDutySchema),
}).strict();
export const ProjectMemberSchema = z.object({
    membershipId: z.uuid(), personId: z.uuid(), personCode: z.string().min(1).max(120),
    displayName: z.string().min(1).max(120), department: z.string().max(120).nullable(),
    jobTitle: z.string().max(120).nullable(), skills: z.array(z.string().min(1).max(60)),
    allocationStatus: AllocationStatusSchema, duties: z.array(ProjectDutySchema).min(1),
    activeTaskCount: z.number().int().nonnegative(), version: z.number().int().positive(),
    effectiveAt: z.iso.datetime(),
}).strict();
export const ProjectMemberListResponseSchema = z.object({ items: z.array(ProjectMemberSchema) }).strict();
export const ProjectMemberOptionSchema = z.object({
    personId: z.uuid(), personCode: z.string().min(1).max(120), displayName: z.string().min(1).max(120),
    department: z.string().max(120).nullable(), jobTitle: z.string().max(120).nullable(),
    skills: z.array(z.string().min(1).max(60)), allocationStatus: AllocationStatusSchema,
    // BUG-0162：候选是否具备全局 PLANNER 基线角色（或被本项目的项目负责人身份覆盖），
    // 用于在选人阶段就阻止「选了内容策划却授予失败」。
    plannerEligible: z.boolean(),
}).strict();
export const ProjectMemberOptionsResponseSchema = z.object({ items: z.array(ProjectMemberOptionSchema) }).strict();
export const AddProjectMemberRequestSchema = z.object({
    personId: z.uuid(), duties: z.array(ProjectDutySchema).min(1).max(10)
        .refine((duties) => new Set(duties).size === duties.length),
}).strict();
export const UpdateProjectMemberRequestSchema = z.object({
    expectedVersion: z.number().int().positive(), duties: z.array(ProjectDutySchema).min(1).max(10)
        .refine((duties) => new Set(duties).size === duties.length),
}).strict();
export const EndProjectMemberRequestSchema = z.object({ expectedVersion: z.number().int().positive() }).strict();
export const ProjectMemberWriteResponseSchema = z.object({
    membershipId: z.uuid(), projectId: z.uuid(), personId: z.uuid(), duties: z.array(ProjectDutySchema),
    version: z.number().int().positive(), active: z.boolean(), changedAt: z.iso.datetime(), replayed: z.boolean(),
}).strict();
export const UpdateProjectRequestSchema = z.object({
    expectedVersion: z.number().int().positive(), name: z.string().trim().min(1).max(120),
    customerId: z.uuid(), primaryManagerId: z.uuid(), description: z.string().trim().max(2000).nullable(),
    reason: z.string().trim().min(2).max(500),
}).strict();
export const SuspendProjectRequestSchema = z.object({
    expectedVersion: z.number().int().positive(), reason: z.string().trim().min(2).max(500),
}).strict();
export const ResumeProjectRequestSchema = z.object({
    expectedVersion: z.number().int().positive(), reason: z.string().trim().min(2).max(500),
}).strict();
export const ProjectWriteResponseSchema = ProjectSummarySchema.extend({ replayed: z.boolean() }).strict();
const PostgreSqlUuidSchema = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
export const ContractProjectLinkSchema = z.object({
    linkId: PostgreSqlUuidSchema, projectId: z.uuid(), projectCode: z.string().min(1), projectName: z.string().min(1),
    projectStatus: ProjectStatusSchema, linkedAt: z.iso.datetime(), linkedByName: z.string().min(1),
}).strict();
export const GlobalContractSummarySchema = z.object({
    contractId: z.uuid(), code: z.string().min(1), name: z.string().min(1).max(120),
    kind: z.enum(['ORIGINAL', 'SUPPLEMENT', 'RENEWAL', 'CORRECTION']), parentContractId: z.uuid().nullable(),
    customerId: z.uuid(), customerCode: z.string().min(1), customerName: z.string().min(1),
    amountKind: z.enum(['FIXED', 'OPEN']), amount: z.string().nullable(), signedOn: z.iso.date().nullable(),
    validFrom: z.iso.date().nullable(), validUntil: z.iso.date().nullable(), status: z.enum(['ARCHIVED', 'VOID']),
    notes: z.string().nullable(), version: z.number().int().positive(), attachmentCount: z.number().int().nonnegative(),
    relatedProjects: z.array(ContractProjectLinkSchema), createdAt: z.iso.datetime(), updatedAt: z.iso.datetime(),
}).strict();
export const GlobalContractListResponseSchema = z.object({
    items: z.array(GlobalContractSummarySchema), nextCursor: z.string().nullable(),
}).strict();
const GlobalContractFieldsSchema = z.object({
    name: z.string().trim().min(1).max(120), kind: z.enum(['ORIGINAL', 'SUPPLEMENT', 'RENEWAL', 'CORRECTION']),
    parentContractId: z.uuid().nullable(), customerId: z.uuid(), amountKind: z.enum(['FIXED', 'OPEN']),
    amount: z.string().regex(/^(?:0|[1-9]\d{0,15})(?:\.\d{1,2})?$/).nullable(),
    signedOn: z.iso.date().nullable(), validFrom: z.iso.date().nullable(), validUntil: z.iso.date().nullable(),
    notes: z.string().trim().max(2000).nullable(), reason: z.string().trim().min(2).max(500),
});
export const CreateGlobalContractRequestSchema = GlobalContractFieldsSchema.extend({
    expectedVersion: z.literal(0), projectIds: z.array(z.uuid()).max(50)
        .refine((ids) => new Set(ids).size === ids.length),
}).strict();
export const LinkContractProjectRequestSchema = z.object({
    expectedVersion: z.number().int().positive(), projectId: z.uuid(), reason: z.string().trim().min(2).max(500),
}).strict();
export const UnlinkContractProjectRequestSchema = z.object({
    expectedVersion: z.number().int().positive(), reason: z.string().trim().min(2).max(500),
}).strict();
export const GlobalContractWriteResponseSchema = GlobalContractSummarySchema.extend({ replayed: z.boolean() }).strict();
export const CustomerResponsibilitySchema = z.object({
    personId: z.uuid(), personCode: z.string().min(1).max(120),
    displayName: z.string().min(1).max(120), version: z.number().int().positive(),
}).strict();
export const CustomerTypeSchema = z.enum([
    'UNSPECIFIED', 'MEDICAL_INSTITUTION', 'DOCTOR_BRAND', 'ENTERPRISE', 'INTERNAL', 'OTHER',
]);
export const CustomerStatusSchema = z.enum(['PROSPECT', 'ACTIVE', 'INACTIVE']);
export const CustomerContactSchema = z.object({
    contactId: z.uuid(), customerId: z.uuid(), name: z.string().min(1).max(60),
    title: z.string().min(1).max(80), phone: z.string().max(40).nullable(),
    email: z.email().max(160).nullable(), note: z.string().max(1000).nullable(),
    isPrimary: z.boolean(), version: z.number().int().positive(),
    createdAt: z.iso.datetime(), updatedAt: z.iso.datetime(),
}).strict();
export const CustomerBillingProfileSchema = z.object({
    profileId: z.uuid(), customerId: z.uuid(), revision: z.number().int().positive(),
    invoiceTitle: z.string().max(160).nullable(), taxNo: z.string().max(60).nullable(),
    bankName: z.string().max(160).nullable(), bankAccount: z.string().max(80).nullable(),
    address: z.string().max(240).nullable(), effectiveAt: z.iso.datetime(),
}).strict();
export const CustomerSummarySchema = z.object({
    customerId: z.uuid(), code: z.string().min(1).max(120),
    name: z.string().min(1).max(120), legalName: z.string().max(160).nullable(),
    // 医院与科室是客户主档的定位字段；迁移数据里可能为空，业务需可维护。
    organization: z.string().max(120).nullable(), department: z.string().max(120).nullable(),
    customerType: CustomerTypeSchema, status: CustomerStatusSchema,
    region: z.string().max(80).nullable(), address: z.string().max(240).nullable(),
    notes: z.string().max(2000).nullable(), version: z.number().int().positive(),
    responsibleBusinesses: z.array(CustomerResponsibilitySchema).max(20),
    contacts: z.array(CustomerContactSchema), billingProfile: CustomerBillingProfileSchema.nullable(),
    projectCount: z.number().int().nonnegative(), activeProjectCount: z.number().int().nonnegative(),
    contractCount: z.number().int().nonnegative(), createdAt: z.iso.datetime(), updatedAt: z.iso.datetime(),
}).strict();
export const CustomerListResponseSchema = z.object({
    items: z.array(CustomerSummarySchema), nextCursor: z.string().min(1).max(120).nullable(),
}).strict();
export const CreateCustomerRequestSchema = z.object({
    expectedVersion: z.literal(0), name: z.string().trim().min(1).max(120),
    legalName: z.string().trim().max(160).nullable(), customerType: CustomerTypeSchema,
    organization: z.string().trim().max(120).nullable(), department: z.string().trim().max(120).nullable(),
    status: CustomerStatusSchema, region: z.string().trim().max(80).nullable(),
    address: z.string().trim().max(240).nullable(), notes: z.string().trim().max(2000).nullable(),
    responsiblePersonIds: z.array(z.uuid()).max(20).refine((ids) => new Set(ids).size === ids.length),
}).strict();
export const UpdateCustomerRequestSchema = z.object({
    expectedVersion: z.number().int().positive(), legalName: z.string().trim().max(160).nullable(),
    // 允许修正客户显示名称：重复建档（如「血液科」并入「血液内科」）需要改名，历史合同快照不受影响。
    name: z.string().trim().min(1).max(120).optional(),
    customerType: CustomerTypeSchema, status: CustomerStatusSchema,
    organization: z.string().trim().max(120).nullable(), department: z.string().trim().max(120).nullable(),
    region: z.string().trim().max(80).nullable(), address: z.string().trim().max(240).nullable(),
    notes: z.string().trim().max(2000).nullable(),
    responsiblePersonIds: z.array(z.uuid()).max(20).refine((ids) => new Set(ids).size === ids.length),
}).strict();
export const CustomerWriteResponseSchema = CustomerSummarySchema.extend({ replayed: z.boolean() }).strict();
const CustomerContactFieldsSchema = z.object({
    name: z.string().trim().min(1).max(60), title: z.string().trim().min(1).max(80),
    phone: z.string().trim().max(40).nullable(), email: z.email().max(160).nullable(),
    note: z.string().trim().max(1000).nullable(), isPrimary: z.boolean(),
});
export const CreateCustomerContactRequestSchema = CustomerContactFieldsSchema.extend({
    expectedCustomerVersion: z.number().int().positive(),
}).strict().refine((value) => Boolean(value.phone || value.email));
export const UpdateCustomerContactRequestSchema = CustomerContactFieldsSchema.extend({
    expectedVersion: z.number().int().positive(),
}).strict().refine((value) => Boolean(value.phone || value.email));
export const RemoveCustomerContactRequestSchema = z.object({
    expectedVersion: z.number().int().positive(),
}).strict();
export const SetPrimaryCustomerContactRequestSchema = z.object({
    expectedCustomerVersion: z.number().int().positive(), contactId: z.uuid(),
}).strict();
export const CustomerContactWriteResponseSchema = z.object({
    contact: CustomerContactSchema, customerVersion: z.number().int().positive(), replayed: z.boolean(),
}).strict();
export const SaveCustomerBillingProfileRequestSchema = z.object({
    expectedCustomerVersion: z.number().int().positive(), invoiceTitle: z.string().trim().max(160).nullable(),
    taxNo: z.string().trim().max(60).nullable(), bankName: z.string().trim().max(160).nullable(),
    bankAccount: z.string().trim().max(80).nullable(), address: z.string().trim().max(240).nullable(),
}).strict();
export const CustomerBillingProfileWriteResponseSchema = z.object({
    profile: CustomerBillingProfileSchema.nullable(), customerVersion: z.number().int().positive(), replayed: z.boolean(),
}).strict();
export const ServiceTemplateContentTypeSchema = z.enum(['VIDEO', 'ARTICLE']);
export const ServiceTemplateStatusSchema = z.enum(['DRAFT', 'PUBLISHED', 'HISTORICAL']);
const ServiceTemplateRateInputSchema = z.string()
    .regex(/^(?:0|[1-9]\d{0,13})(?:\.\d{1,2})?$/);
const ServiceTemplateRateSchema = z.string().regex(/^(?:0|[1-9]\d{0,13})\.\d{2}$/);
const ServiceTemplateKeySchema = z.string().trim().min(1).max(40).regex(/^[A-Z0-9][A-Z0-9_-]*$/);
export const ServiceTemplateTaskSchema = z.object({
    ruleId: z.uuid(), taskKey: ServiceTemplateKeySchema, name: z.string().min(1).max(80),
    description: z.string().min(1).max(500), unit: z.string().min(1).max(20),
    quantity: z.number().int().positive().max(1000), required: z.boolean(),
    rate: ServiceTemplateRateSchema, isSystemPublication: z.boolean(), sortOrder: z.number().int().positive(),
}).strict();
export const ServiceTemplateVariantSchema = z.object({
    variantId: z.uuid(), variantKey: ServiceTemplateKeySchema, name: z.string().min(1).max(80),
    description: z.string().min(1).max(500), sortOrder: z.number().int().positive(),
    groupKey: z.string().nullable().optional(), groupName: z.string().nullable().optional(),
    rateLabel: z.string().nullable().optional(),
    tasks: z.array(ServiceTemplateTaskSchema), productionBudget: ServiceTemplateRateSchema,
    publicationBudget: ServiceTemplateRateSchema, totalBudget: ServiceTemplateRateSchema,
}).strict();
export const ServiceTemplateVersionSummarySchema = z.object({
    familyId: z.uuid(), familyCode: z.string().min(1), familyName: z.string().min(1),
    versionId: z.uuid(), versionNo: z.number().int().positive(), version: z.number().int().positive(),
    contentType: ServiceTemplateContentTypeSchema, status: ServiceTemplateStatusSchema, archived: z.boolean(),
    versionName: z.string().min(1).max(120), deliverable: z.string().min(1).max(2000),
    defaultCapacity: z.number().int().positive().max(100000), effectiveOn: z.iso.date(),
    draftedBy: z.uuid(), publishedBy: z.uuid().nullable(), publishedAt: z.iso.datetime().nullable(),
    variantCount: z.number().int().nonnegative(), productionTaskCount: z.number().int().nonnegative(),
    sourceTemplateVersionId: z.uuid().nullable(), sourceRateVersionId: z.uuid().nullable(),
}).strict();
export const ServiceTemplateVersionDetailSchema = ServiceTemplateVersionSummarySchema.extend({
    variants: z.array(ServiceTemplateVariantSchema),
}).strict();
export const ServiceTemplateListResponseSchema = z.object({
    items: z.array(ServiceTemplateVersionSummarySchema), nextCursor: z.string().nullable(),
}).strict();
const ServiceTemplateTaskDraftSchema = z.object({
    taskKey: ServiceTemplateKeySchema, name: z.string().trim().min(1).max(80),
    description: z.string().trim().max(500).default(''), unit: z.string().trim().min(1).max(20),
    quantity: z.number().int().positive().max(1000), required: z.boolean(), rate: ServiceTemplateRateInputSchema,
}).strict();
const ServiceTemplateVariantDraftSchema = z.object({
    variantKey: ServiceTemplateKeySchema, name: z.string().trim().min(1).max(80),
    description: z.string().trim().min(1).max(500),
    tasks: z.array(ServiceTemplateTaskDraftSchema).max(50),
}).strict().transform((variant) => ({ ...variant,
    tasks: variant.tasks.map((task) => ({ ...task, description: task.description || task.name })) }));
const ServiceTemplateVersionDraftFieldsSchema = z.object({
    versionName: z.string().trim().min(1).max(120), deliverable: z.string().trim().min(1).max(2000),
    defaultCapacity: z.number().int().positive().max(100000), effectiveOn: z.iso.date(),
    variants: z.array(ServiceTemplateVariantDraftSchema).min(1).max(30),
});
export const CreateServiceTemplateDraftRequestSchema = ServiceTemplateVersionDraftFieldsSchema.extend({
    expectedVersion: z.literal(0), familyName: z.string().trim().min(1).max(120),
    contentType: ServiceTemplateContentTypeSchema,
}).strict();
export const UpdateServiceTemplateDraftRequestSchema = ServiceTemplateVersionDraftFieldsSchema.extend({
    expectedVersion: z.number().int().positive(),
}).strict();
export const CloneServiceTemplateRequestSchema = z.object({
    expectedVersion: z.number().int().positive(), effectiveOn: z.iso.date(),
}).strict();
export const PublishServiceTemplateRequestSchema = z.object({
    expectedVersion: z.number().int().positive(),
}).strict();
export const ServiceTemplateWriteResponseSchema = ServiceTemplateVersionSummarySchema.extend({
    replayed: z.boolean(),
}).strict();
export const ServiceTemplatePublishPreviewSchema = z.object({
    versionId: z.uuid(), comparedToVersionId: z.uuid().nullable(),
    changes: z.array(z.object({ field: z.string(), before: z.string().nullable(), after: z.string().nullable() }).strict()),
    impact: z.object({ versionsPublished: z.literal(1), existingPackagesChanged: z.literal(0),
        futurePackagesUseNewVersion: z.boolean() }).strict(),
}).strict();
export const StandardKindSchema = z.enum(['TEMPLATE', 'RATE']);
export const StandardContentTypeSchema = z.enum(['VIDEO', 'ARTICLE']);
export const StandardVersionSummarySchema = z.object({
    familyId: z.uuid(), familyCode: z.string(), familyName: z.string(), versionId: z.uuid(),
    versionNo: z.number().int().positive(), version: z.number().int().positive(),
    kind: StandardKindSchema, contentType: StandardContentTypeSchema,
    status: z.enum(['DRAFT', 'PUBLISHED', 'WITHDRAWN']), effectiveOn: z.iso.date(),
    draftedBy: z.uuid(), confirmerId: z.uuid(), confirmedAt: z.iso.datetime().nullable(),
    templateVersionId: z.uuid().nullable(), defaultCapacity: z.number().int().positive().nullable(),
    unit: z.string().nullable(), acceptanceStandard: z.string().nullable(),
    revisionStandard: z.string().nullable(), reason: z.string().nullable(),
}).strict();
export const StandardVersionDetailSchema = StandardVersionSummarySchema.extend({
    tiers: z.array(z.object({ code: z.string(), name: z.string() }).strict()),
    tasks: z.array(z.object({ code: z.string(), name: z.string() }).strict()),
    rules: z.array(z.object({ tierCode: z.string(), taskCode: z.string(), amount: z.string().regex(/^\d+\.\d{4}$/) }).strict()),
}).strict();
export const StandardListResponseSchema = z.object({ items: z.array(StandardVersionSummarySchema),
    nextCursor: z.string().nullable() }).strict();
export const StandardDraftRequestSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('TEMPLATE'), familyId: z.uuid().optional(), expectedVersion: z.number().int().nonnegative(),
        contentType: StandardContentTypeSchema, familyName: z.string().trim().min(1).max(120), effectiveOn: z.iso.date(),
        confirmerId: z.uuid(), defaultCapacity: z.number().int().positive(), unit: z.string().trim().min(1).max(20),
        acceptanceStandard: z.string().trim().min(1).max(2000), revisionStandard: z.string().trim().min(1).max(2000),
        tiers: z.array(z.object({ code: z.string().trim().min(1).max(40), name: z.string().trim().min(1).max(80) }).strict()).min(1),
        tasks: z.array(z.object({ code: z.string().trim().min(1).max(40), name: z.string().trim().min(1).max(80) }).strict()).min(1),
    }).strict(),
    z.object({ kind: z.literal('RATE'), familyId: z.uuid().optional(), expectedVersion: z.number().int().nonnegative(),
        contentType: StandardContentTypeSchema, familyName: z.string().trim().min(1).max(120), effectiveOn: z.iso.date(),
        confirmerId: z.uuid(), templateVersionId: z.uuid(), reason: z.string().trim().min(1).max(500),
        rules: z.array(z.object({ tierCode: z.string().trim().min(1).max(40), taskCode: z.string().trim().min(1).max(40),
            amount: z.string().regex(/^(?:0|[1-9]\d{0,13})(?:\.\d{1,4})?$/) }).strict()).min(1),
    }).strict(),
]);
const ServicePackageAmountInputSchema = z.string().regex(/^(?:0|[1-9]\d{0,15})(?:\.\d{1,2})?$/);
const ServicePackageAmountSchema = z.string().regex(/^(?:0|[1-9]\d{0,15})\.\d{2}$/);
export const ServicePackageStatusSchema = z.enum([
    'DRAFT', 'SUBMITTED', 'RETURNED', 'CONFIRMED', 'ACTIVE', 'COMPLETED',
]);
const VideoPackageDraftFieldsSchema = z.object({
    serviceTemplateVersionId: z.uuid(), businessOn: z.iso.date(),
    packageTitle: z.string().trim().min(1).max(120).nullable().optional(),
    periodFrom: z.iso.date(), periodUntil: z.iso.date(),
    capacity: z.number().int().positive().max(100000),
    variants: z.array(z.object({ variantKey: ServiceTemplateKeySchema,
        count: z.number().int().nonnegative().max(100000) }).strict()).min(1).max(30)
        .refine((rows) => new Set(rows.map((row) => row.variantKey)).size === rows.length),
    flexibleReserve: ServicePackageAmountInputSchema,
    managerId: z.uuid(), confirmerId: z.uuid(),
});
// 新服务包只引用 S3 统一服务模板。生产费用、发布预算与总预算均由服务端计算，
// 调用方不能提交合计金额或标准快照。
export const VideoPackageDraftRequestSchema = VideoPackageDraftFieldsSchema.extend({
    expectedVersion: z.literal(0),
}).strict();
export const VideoPackageUpdateDraftRequestSchema = VideoPackageDraftFieldsSchema.extend({
    expectedVersion: z.number().int().positive(),
}).strict();
export const VideoPackageDefinitionUpdateRequestSchema = z.object({
    expectedVersion: z.number().int().positive(),
    packageTitle: z.string().trim().min(1).max(120).nullable(),
    businessOn: z.iso.date(), periodFrom: z.iso.date(), periodUntil: z.iso.date(),
    capacity: z.number().int().positive().max(100000),
    variants: z.array(z.object({ variantKey: ServiceTemplateKeySchema,
        count: z.number().int().nonnegative().max(100000) }).strict()).min(1).max(100)
        .refine((rows) => new Set(rows.map((row) => row.variantKey)).size === rows.length),
    flexibleReserve: ServicePackageAmountInputSchema,
    managerId: z.uuid(), confirmerId: z.uuid(),
    reason: z.string().trim().min(2).max(500),
}).strict();
export const VideoPackageSubmitRequestSchema = z.object({ expectedVersion: z.number().int().positive() }).strict();
export const VideoPackageReturnRequestSchema = z.object({ expectedVersion: z.number().int().positive(),
    reason: z.string().trim().min(2).max(500) }).strict();
export const VideoPackageConfirmRequestSchema = z.object({ expectedVersion: z.number().int().positive() }).strict();
export const VideoPackageStartRequestSchema = z.object({ expectedVersion: z.number().int().positive() }).strict();
export const VideoPackageCompleteRequestSchema = z.object({ expectedVersion: z.number().int().positive(),
    completedOn: z.iso.date(), note: z.string().trim().min(2).max(1000) }).strict();
export const VideoPackageReopenRequestSchema = z.object({ expectedVersion: z.number().int().positive(),
    reason: z.string().trim().min(2).max(500) }).strict();
export const VideoTaskAssignRequestSchema = z.object({
    expectedVersion: z.number().int().positive(), tierCode: z.string().trim().min(1).max(40),
    taskCode: z.string().trim().min(1).max(40), executorId: z.uuid(),
}).strict();
export const ProductionTaskStatusSchema = z.enum([
    'UNASSIGNED', 'ASSIGNED', 'ACCEPTED', 'IN_PROGRESS', 'SUBMITTED', 'COMPLETED', 'VOID',
]);
export const VideoTaskProgressRequestSchema = z.object({
    expectedVersion: z.number().int().positive(), status: z.enum(['ACCEPTED', 'IN_PROGRESS']),
}).strict();
export const ContentDisplayNoSchema = z.number().int().min(1).max(999_999_999);
export const UpdateContentTitleRequestSchema = z.object({
    expectedVersion: z.number().int().positive(), title: z.string().trim().min(1).max(160),
    legacyNo: z.string().trim().regex(/^[0-9]{1,9}$/).optional(),
    displayNo: ContentDisplayNoSchema.optional(),
}).strict();
// BUG-0142：内容策划提前填写发布信息，新建发布计划时带出。
export const ContentPublicationInfoSchema = z.object({
    title: z.string().max(200).nullable(), copy: z.string().max(5000).nullable(),
    tags: z.array(z.string().max(40)).max(30),
}).strict();
export const UpdateContentPublicationInfoRequestSchema = z.object({
    expectedVersion: z.number().int().positive(),
    publicationTitle: z.string().trim().min(1).max(200).nullable(),
    publicationCopy: z.string().max(5000),
    publicationTags: z.array(z.string().trim().min(1).max(40)).max(30),
}).strict();
export const VideoTaskProgressResponseSchema = z.object({
    taskId: z.uuid(), projectId: z.uuid(), packageId: z.uuid(), contentId: z.uuid(),
    status: ProductionTaskStatusSchema, version: z.number().int().positive(),
    changedAt: z.iso.datetime(), replayed: z.boolean(),
}).strict();
export const VideoResultSubmitRequestSchema = z.object({ expectedVersion: z.number().int().positive(),
    stagingId: z.uuid() }).strict();
export const VideoResultStageReceiptSchema = z.object({ stagingId: z.uuid(), projectId: z.uuid(),
    taskId: z.uuid(), originalFilename: z.string().min(1).max(255),
    detectedMime: z.enum(['video/mp4', 'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain', 'text/markdown',
        'image/png', 'image/jpeg']),
    byteSize: z.number().int().positive().max(20_971_520),
    sha256: z.string().regex(/^[0-9a-f]{64}$/), expiresAt: z.iso.datetime(), replayed: z.boolean() }).strict();
export const VideoResultReceiptSchema = z.object({ resultId: z.uuid(), projectId: z.uuid(),
    packageId: z.uuid(), contentId: z.uuid(), taskId: z.uuid(), versionNo: z.number().int().positive(),
    taskVersion: z.number().int().positive(), originalFilename: z.string().min(1).max(255),
    detectedMime: z.enum(['video/mp4', 'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain', 'text/markdown']),
    byteSize: z.number().int().positive().max(20_971_520), sha256: z.string().regex(/^[0-9a-f]{64}$/),
    submittedAt: z.iso.datetime(), replayed: z.boolean().optional() }).strict();
export const VideoResultListSchema = z.object({ items: z.array(VideoResultReceiptSchema) }).strict();
export const VideoDeliveryAcceptRequestSchema = z.object({ expectedVersion: z.number().int().positive(),
    resultId: z.uuid(), businessOn: z.iso.date(), channel: z.string().trim().min(1).max(120) }).strict();
export const VideoDeliveryReceiptSchema = z.object({ deliveryId: z.uuid(), projectId: z.uuid(),
    packageId: z.uuid(), contentId: z.uuid(), resultId: z.uuid(), businessOn: z.iso.date(),
    channel: z.string(), confirmedAt: z.iso.datetime(), confirmedBy: z.uuid(), taskCount: z.number().int().positive(),
    committedAmount: z.string(), accruedAmount: z.string(), accrualRows: z.number().int().nonnegative(),
    sourceStatus: z.literal('PENDING_FINANCE'), replayed: z.boolean().optional() }).strict();
export const VideoDeliveryDetailSchema = z.object({ delivery: VideoDeliveryReceiptSchema.nullable() }).strict();
export const VideoContentAuditSchema = z.object({ items: z.array(z.object({ eventId: z.uuid(),
        action: z.string(), targetId: z.uuid(), actorId: z.uuid().nullable(), occurredAt: z.iso.datetime() }).strict()) }).strict();
export const ContentPublicationTaskSchema = z.object({
    taskCode: z.literal('PUBLICATION'), taskName: z.literal('发布'), planId: z.uuid().nullable(),
    mode: z.enum(['INTERNAL', 'CUSTOMER']).nullable(),
    status: z.enum(['UNPLANNED', 'PLANNED', 'IN_PROGRESS', 'COMPLETED', 'VOID']),
    lockedAmount: z.string().nullable().optional(),
    assignees: z.array(z.object({ assignmentId: z.uuid(), personId: z.uuid(), displayName: z.string(),
        role: z.enum(['PRIMARY', 'COLLABORATOR']), shareBps: z.number().int().min(1).max(10000).optional(),
        assignedAt: z.iso.datetime() }).strict()),
}).strict();
export const VideoContentSummarySchema = z.object({
    contentId: z.uuid(), contentCode: z.string(), legacyNo: z.string().max(40).nullable(),
    displayNo: ContentDisplayNoSchema.nullable().default(null),
    displayNoLocked: z.boolean().optional(),
    title: z.string().min(1).max(160).nullable(), createdAt: z.iso.datetime(),
    status: z.string(), version: z.number().int().positive(),
    issueStats: z.object({ open: z.number().int().nonnegative(), blocking: z.number().int().nonnegative(),
        mineOpen: z.number().int().nonnegative() }).strict(),
    tierCode: z.string().nullable(), tasks: z.array(z.object({ taskId: z.uuid(), taskCode: z.string(),
        taskName: z.string(), status: ProductionTaskStatusSchema, version: z.number().int().positive(),
        executorId: z.uuid().nullable(), executorName: z.string().nullable(), assignedAt: z.iso.datetime().nullable(),
        returnReason: z.string().nullable().optional(), returnedAt: z.iso.datetime().nullable().optional(),
        returnedByName: z.string().nullable().optional(),
        // BUG-0172 B2：受限角色（执行人/策划/混合）直接省略这些字段，不再返回 null。
        payeeId: z.uuid().nullable().optional(), shareBps: z.number().int().nullable().optional(),
        lockedAmount: z.string().nullable().optional(),
        assignees: z.array(z.object({ assignmentId: z.uuid(), personId: z.uuid(), displayName: z.string(),
            role: z.enum(['PRIMARY', 'COLLABORATOR']), shareBps: z.number().int().min(1).max(10000).optional(),
            assignedAt: z.iso.datetime() }).strict()).default([]) }).strict()),
    publicationTask: ContentPublicationTaskSchema.nullable().default(null),
    publicationInfo: ContentPublicationInfoSchema.default({ title: null, copy: null, tags: [] }),
}).strict();
export const UpdateContentTitleResponseSchema = VideoContentSummarySchema.extend({ replayed: z.boolean() }).strict();
export const UpdateContentPublicationInfoResponseSchema = VideoContentSummarySchema.extend({ replayed: z.boolean() }).strict();
export const ContentIssueStatusSchema = z.enum(['OPEN', 'RESOLVED', 'VOID']);
export const ContentIssueCategorySchema = z.enum(['PLANNING', 'SHOOTING', 'EDITING', 'COPY', 'PUBLISHING', 'OTHER']);
export const ContentIssueAttachmentSchema = z.object({
    attachmentId: z.uuid(), stagingId: z.uuid(), originalFilename: z.string().min(1).max(255),
    detectedMime: z.enum(['application/pdf', 'image/png', 'image/jpeg']),
    byteSize: z.number().int().positive(), sha256: z.string().regex(/^[0-9a-f]{64}$/),
}).strict();
export const ContentIssueSchema = z.object({
    issueId: z.uuid(), projectId: z.uuid(), packageId: z.uuid(), contentId: z.uuid(),
    contentType: z.enum(['VIDEO', 'ARTICLE']), contentCode: z.string(), packageCode: z.string(),
    sourceResultId: z.uuid().nullable(), sourceVersion: z.number().int().positive().nullable(),
    location: z.string().nullable(), category: ContentIssueCategorySchema,
    title: z.string().min(1).max(160), description: z.string().nullable(), status: ContentIssueStatusSchema,
    blocking: z.boolean(), reporterId: z.uuid(), assigneeId: z.uuid().nullable(),
    resolvedBy: z.uuid().nullable(), resolvedAt: z.iso.datetime().nullable(),
    voidedBy: z.uuid().nullable(), voidedAt: z.iso.datetime().nullable(), voidReason: z.string().nullable(),
    attachments: z.array(ContentIssueAttachmentSchema).default([]),
    version: z.number().int().positive(), createdAt: z.iso.datetime(), updatedAt: z.iso.datetime(),
}).strict();
export const ContentIssueListSchema = z.object({ items: z.array(ContentIssueSchema) }).strict();
export const CreateContentIssueRequestSchema = z.object({
    expectedVersion: z.number().int().positive(), sourceResultId: z.uuid().nullable().optional(),
    location: z.string().trim().min(1).max(160).nullable().optional(), category: ContentIssueCategorySchema,
    title: z.string().trim().min(1).max(160), description: z.string().trim().max(2000).nullable().optional(),
    assigneeId: z.uuid().nullable().optional(), blocking: z.boolean().default(false),
    evidenceStagingIds: z.array(z.uuid()).max(5).default([]),
}).strict().superRefine((value, context) => {
    if (new Set(value.evidenceStagingIds).size !== value.evidenceStagingIds.length)
        context.addIssue({ code: 'custom', path: ['evidenceStagingIds'], message: '问题截图不能重复' });
});
export const UpdateContentIssueAssigneeRequestSchema = z.object({
    expectedVersion: z.number().int().positive(), assigneeId: z.uuid().nullable(),
}).strict();
export const UpdateContentIssueBlockingRequestSchema = z.object({
    expectedVersion: z.number().int().positive(), blocking: z.boolean(),
}).strict();
export const TransitionContentIssueRequestSchema = z.object({ expectedVersion: z.number().int().positive() }).strict();
export const VoidContentIssueRequestSchema = z.object({ expectedVersion: z.number().int().positive(),
    reason: z.string().trim().min(1).max(500) }).strict();
export const ContentIssueWriteResponseSchema = ContentIssueSchema.extend({ replayed: z.boolean() }).strict();
export const VideoTaskAssignResponseSchema = VideoContentSummarySchema.extend({ replayed: z.boolean() }).strict();
export const VideoContentListSchema = z.object({ items: z.array(VideoContentSummarySchema),
    nextCursor: z.number().int().positive().nullable() }).strict();
export const ContentCapacitySchema = z.object({
    packageId: z.uuid(), capacity: z.number().int().nonnegative(), used: z.number().int().nonnegative(),
    remaining: z.number().int().nonnegative(), nextDisplayNo: ContentDisplayNoSchema,
    variants: z.array(z.object({
        variantKey: z.string(), name: z.string(), quota: z.number().int().nonnegative(),
        used: z.number().int().nonnegative(), remaining: z.number().int().nonnegative(),
    }).strict()),
}).strict();
export const TaskAssigneeInputSchema = z.object({
    personId: z.uuid(), role: z.enum(['PRIMARY', 'COLLABORATOR']),
    shareBps: z.number().int().min(1).max(10000),
}).strict();
const TaskAssigneeListSchema = z.array(TaskAssigneeInputSchema).min(1).max(20)
    .refine((rows) => rows.filter((row) => row.role === 'PRIMARY').length === 1, { message: 'Exactly one primary assignee is required' })
    .refine((rows) => new Set(rows.map((row) => row.personId)).size === rows.length, { message: 'Task assignees must be unique' })
    .refine((rows) => rows.reduce((sum, row) => sum + row.shareBps, 0) === 10000, { message: 'Task assignee shares must total 10000' });
export const CreateContentItemSchema = z.object({
    title: z.string().trim().min(1).max(160), variantKey: z.string().trim().min(1).max(40),
    displayNo: ContentDisplayNoSchema.optional(),
    assignments: z.array(z.object({ taskKey: z.string().trim().min(1).max(40),
        assignees: TaskAssigneeListSchema }).strict()).max(100).default([]),
}).strict();
export const CreateContentRequestSchema = CreateContentItemSchema;
export const CreateContentsBatchRequestSchema = z.object({ items: z.array(CreateContentItemSchema).min(1).max(50) }).strict();
export const CreateContentsResponseSchema = z.object({ items: z.array(VideoContentSummarySchema),
    capacity: ContentCapacitySchema, replayed: z.boolean() }).strict();
export const VoidContentRequestSchema = z.object({ expectedVersion: z.number().int().positive(),
    reason: z.string().trim().min(2).max(500) }).strict();
export const VoidContentResponseSchema = z.object({ contentId: z.uuid(), projectId: z.uuid(),
    packageId: z.uuid(), status: z.literal('VOID'), version: z.number().int().positive(),
    capacity: ContentCapacitySchema, replayed: z.boolean() }).strict();
export const AssignTaskV2RequestSchema = z.object({
    expectedVersion: z.number().int().positive(), assignees: TaskAssigneeListSchema,
    reason: z.string().trim().min(2).max(500).optional(),
}).strict();
export const TransitionTaskV2RequestSchema = z.object({ expectedVersion: z.number().int().positive(),
    reason: z.string().trim().min(2).max(500).optional() }).strict();
export const ReturnSubmittedTaskRequestSchema = z.object({ expectedVersion: z.number().int().positive(),
    reason: z.string().trim().min(2).max(500) }).strict();
export const TaskWriteResponseSchema = z.object({ taskId: z.uuid(), contentId: z.uuid(), packageId: z.uuid(),
    projectId: z.uuid(), status: ProductionTaskStatusSchema, version: z.number().int().positive(),
    replayed: z.boolean() }).strict();
export const TaskMaterialSchema = z.object({
    materialId: z.uuid(), contentId: z.uuid(), taskId: z.uuid().nullable(),
    sourceTaskCode: z.string().nullable(), sourceTaskName: z.string().nullable(),
    kind: z.enum(['FILE', 'LINK', 'MARKDOWN']), status: z.literal('ACTIVE'), version: z.number().int().positive(),
    title: z.string(), description: z.string().nullable(), createdBy: z.uuid(), createdAt: z.iso.datetime(),
    canEdit: z.boolean(), canDelete: z.boolean(), downloadPath: z.string().nullable(),
    file: z.object({ stagingId: z.uuid(), originalFilename: z.string(), detectedMime: z.string(),
        byteSize: z.number().int().nonnegative(), sha256: z.string(),
        versions: z.array(z.object({ versionNo: z.number().int().positive(),
            action: z.enum(['CREATE', 'REPLACE', 'RETITLE', 'RETRACT']), title: z.string(), stagingId: z.uuid(),
            originalFilename: z.string(), detectedMime: z.string(), byteSize: z.number().int().nonnegative(),
            sha256: z.string(), createdAt: z.iso.datetime() }).strict()) }).nullable(),
    linkVersions: z.array(z.object({ versionNo: z.number().int().positive(), url: z.url(), title: z.string(),
        description: z.string().nullable(), createdAt: z.iso.datetime() }).strict()),
    markdown: z.object({ draft: z.string(), draftVersion: z.number().int().positive(),
        updatedAt: z.iso.datetime(), versions: z.array(z.object({ versionNo: z.number().int().positive(),
            sourceDraftVersion: z.number().int().positive(), markdown: z.string(), publishedAt: z.iso.datetime() }).strict()) }).nullable(),
}).strict();
export const TaskMaterialListSchema = z.object({ items: z.array(TaskMaterialSchema) }).strict();
export const AddTaskFileMaterialRequestSchema = z.object({ taskVersion: z.number().int().positive(), stagingId: z.uuid(),
    title: z.string().trim().min(1).max(160), description: z.string().trim().max(1000).nullable().optional() }).strict();
export const AddTaskLinkMaterialRequestSchema = z.object({ taskVersion: z.number().int().positive(),
    url: z.url().max(2000), title: z.string().trim().min(1).max(160),
    description: z.string().trim().max(1000).nullable().optional(), materialId: z.uuid().optional() }).strict();
export const SaveTaskMarkdownDraftRequestSchema = z.object({ expectedDraftVersion: z.number().int().nonnegative(),
    materialId: z.uuid().optional(), title: z.string().trim().min(1).max(160).optional(),
    markdown: z.string().max(1000000) }).strict();
export const PublishTaskMarkdownVersionRequestSchema = z.object({ materialId: z.uuid(),
    expectedDraftVersion: z.number().int().positive() }).strict();
export const TaskMaterialWriteResponseSchema = z.object({ material: TaskMaterialSchema,
    replayed: z.boolean() }).strict();
export const DeleteContentMaterialRequestSchema = z.object({ expectedVersion: z.number().int().positive(),
    reason: z.string().trim().min(2).max(500) }).strict();
export const DeleteContentMaterialResponseSchema = z.object({ materialId: z.uuid(), contentId: z.uuid(),
    status: z.literal('DELETED'), version: z.number().int().positive(), replayed: z.boolean() }).strict();
export const RegisterDeliveryRequestSchema = z.object({
    contentVersion: z.number().int().positive(), actualDeliveredOn: z.iso.date(),
    channel: z.string().trim().min(1).max(120), note: z.string().trim().max(1000).nullable().optional(),
    evidenceStagingIds: z.array(z.uuid()).min(1).max(20)
        .refine((ids) => new Set(ids).size === ids.length),
}).strict();
export const RegisterDeliveriesBatchRequestSchema = z.object({ items: z.array(z.object({ contentId: z.uuid() })
        .extend(RegisterDeliveryRequestSchema.shape).strict()).min(1).max(50) }).strict();
export const DeliveryRecordSchema = z.object({
    deliveryId: z.uuid(), contentId: z.uuid(), contentCode: z.string(), projectId: z.uuid(), packageId: z.uuid(),
    versionNo: z.number().int().positive(), version: z.number().int().positive(),
    status: z.enum(['CURRENT', 'WITHDRAWN', 'SUPERSEDED', 'VOID']), current: z.boolean(),
    actualDeliveredOn: z.iso.date(), channel: z.string(), note: z.string().nullable(),
    registeredBy: z.uuid(), registeredAt: z.iso.datetime(), withdrawalReason: z.string().nullable(),
    evidence: z.array(z.object({ evidenceId: z.uuid(), stagingId: z.uuid(), originalFilename: z.string(),
        detectedMime: z.string(), byteSize: z.number().int().nonnegative(), sha256: z.string() }).strict()),
    feeGroup: z.object({ feeGroupId: z.uuid(), status: z.enum(['PENDING', 'FROZEN', 'PARTIALLY_SETTLED', 'SETTLED', 'VOID']),
        actualOn: z.iso.date(), totalAmount: z.string(), settledAmount: z.string() }).nullable(),
}).strict();
export const DeliveryRecordListSchema = z.object({ items: z.array(DeliveryRecordSchema) }).strict();
export const DeliveryWriteResponseSchema = z.object({ delivery: DeliveryRecordSchema,
    replayed: z.boolean() }).strict();
export const DeliveryBatchResponseSchema = z.object({ results: z.array(z.discriminatedUnion('success', [
        z.object({ success: z.literal(true), contentId: z.uuid(), delivery: DeliveryRecordSchema }).strict(),
        z.object({ success: z.literal(false), contentId: z.uuid(), code: z.string(), message: z.string() }).strict(),
    ])), replayed: z.boolean() }).strict();
export const ChangeImpactPreviewSchema = z.object({
    previewId: z.uuid(), previewVersion: z.number().int().positive(), kind: z.enum([
        'DELIVERY_WITHDRAW', 'DELIVERY_CORRECT', 'VARIANT_CHANGE', 'TASK_VOID', 'CONTENT_VOID',
    ]), targetId: z.uuid(), targetVersion: z.number().int().positive(), expiresAt: z.iso.datetime(),
    blockers: z.array(z.object({ code: z.string(), label: z.string(), href: z.string().optional() }).strict()),
    impact: z.object({ feeGroupId: z.uuid().nullable(), currentAmount: z.string(), settledAmount: z.string(),
        handling: z.enum(['FREEZE', 'RECALCULATE', 'NEXT_PERIOD_ADJUSTMENT', 'NO_FEE_CHANGE']),
        affectedTasks: z.number().int().nonnegative(), affectedSettlements: z.number().int().nonnegative() }).strict(),
}).strict();
export const PreviewChangeRequestSchema = z.object({ expectedVersion: z.number().int().positive(),
    reason: z.string().trim().min(2).max(1000) }).strict();
export const ConfirmChangeRequestSchema = PreviewChangeRequestSchema.extend({ previewId: z.uuid(),
    previewVersion: z.number().int().positive() }).strict();
export const CorrectDeliveryRequestSchema = ConfirmChangeRequestSchema.extend({ actualDeliveredOn: z.iso.date(),
    channel: z.string().trim().min(1).max(120), note: z.string().trim().max(1000).nullable().optional(),
    evidenceStagingIds: z.array(z.uuid()).min(1).max(20) }).strict();
export const VariantChangeRequestSchema = ConfirmChangeRequestSchema.extend({
    targetVariantKey: z.string().trim().min(1).max(40),
}).strict();
export const TaskVoidRequestSchema = ConfirmChangeRequestSchema.extend({
    feeDecision: z.enum(['REDUCE', 'KEEP']),
}).strict();
export const BusinessChangeSchema = z.object({ changeId: z.uuid(), projectId: z.uuid(), kind: z.string(),
    targetType: z.string(), targetId: z.uuid(), reason: z.string(), effect: z.record(z.string(), z.unknown()),
    changedBy: z.uuid(), changedAt: z.iso.datetime() }).strict();
export const BusinessChangeListSchema = z.object({ items: z.array(BusinessChangeSchema) }).strict();
const PackageOperationAmountSchema = z.string().regex(/^(?:0|[1-9]\d{0,15})\.\d{2}$/);
export const PublicationRecordRequestSchema = z.object({ contentId: z.uuid(),
    platform: z.string().trim().min(1).max(80), accountId: z.string().trim().min(1).max(120),
    status: z.enum(['SUCCEEDED', 'FAILED']), attemptedOn: z.iso.date() }).strict();
export const PublicationRecordSchema = z.object({ attemptId: z.uuid(), contentId: z.uuid(),
    platform: z.string(), accountId: z.string(), attemptNo: z.number().int().positive(),
    status: z.enum(['SUCCEEDED', 'FAILED']), attemptedOn: z.iso.date(), recordedAt: z.iso.datetime(),
    replayed: z.boolean().optional() }).strict();
export const FlexibleWorkCreateRequestSchema = z.object({ kind: z.enum(['SHOOTING', 'VISUAL', 'OTHER']),
    title: z.string().trim().min(1).max(120), description: z.string().trim().min(1).max(1000),
    amount: PackageOperationAmountSchema.refine((value) => value !== '0.00'), executorId: z.uuid(),
    businessOn: z.iso.date(), contentIds: z.array(z.uuid()).max(100) }).strict();
export const FlexibleWorkOrderSchema = z.object({ workOrderId: z.uuid(), code: z.string(),
    kind: z.enum(['SHOOTING', 'VISUAL', 'OTHER']), title: z.string(), description: z.string(),
    amount: PackageOperationAmountSchema, executorId: z.uuid(), payeeId: z.uuid(), businessOn: z.iso.date(),
    status: z.enum(['OPEN', 'COMPLETED']), contentIds: z.array(z.uuid()), createdBy: z.uuid(),
    confirmedBy: z.uuid().nullable(), confirmedAt: z.iso.datetime().nullable(), replayed: z.boolean().optional() }).strict();
export const ExpenseRecordRequestSchema = z.object({ feishuNo: z.string().trim().min(1).max(120),
    totalAmount: PackageOperationAmountSchema.refine((value) => value !== '0.00'), completedOn: z.iso.date(),
    attachmentRef: z.string().trim().min(1).max(500), note: z.string().trim().min(1).max(1000),
    allocations: z.array(z.object({ packageId: z.uuid(), amount: PackageOperationAmountSchema
            .refine((value) => value !== '0.00') }).strict()).min(1).max(100) }).strict();
export const ExpenseAllocationSchema = z.object({ allocationId: z.uuid(), packageId: z.uuid(),
    amount: PackageOperationAmountSchema, confirmedBy: z.uuid().nullable(), confirmedAt: z.iso.datetime().nullable(),
    replayed: z.boolean().optional() }).strict();
export const ExpenseRecordSchema = z.object({ expenseId: z.uuid(), feishuNo: z.string(),
    totalAmount: PackageOperationAmountSchema, completedOn: z.iso.date(), attachmentRef: z.string(), note: z.string(),
    recordedBy: z.uuid(), recordedAt: z.iso.datetime(), allocations: z.array(ExpenseAllocationSchema),
    replayed: z.boolean().optional() }).strict();
export const ClosurePreviewSchema = z.object({ packageId: z.uuid(), canClose: z.boolean(),
    blockers: z.array(z.object({ code: z.string(), count: z.number().int().positive() }).strict()),
    approvedBudget: PackageOperationAmountSchema, contentCost: PackageOperationAmountSchema,
    flexibleCost: PackageOperationAmountSchema, expenseCost: PackageOperationAmountSchema,
    confirmedCost: PackageOperationAmountSchema, balance: z.string().regex(/^-?(?:0|[1-9]\d{0,15})\.\d{2}$/),
    bonusLimit: PackageOperationAmountSchema, expenseDeclared: z.boolean() }).strict();
export const PackageClosureSchema = ClosurePreviewSchema.extend({ status: z.literal('CLOSED'),
    closedBy: z.uuid(), closedAt: z.iso.datetime(), replayed: z.boolean().optional() }).strict();
export const BonusSubmitRequestSchema = z.object({ adminReviewerId: z.uuid(), finalReviewerId: z.uuid(),
    allocations: z.array(z.object({ personId: z.uuid(), amount: PackageOperationAmountSchema
            .refine((value) => value !== '0.00') }).strict()).min(1).max(100) }).strict();
export const BonusReviewRequestSchema = z.object({ stage: z.enum(['ADMIN', 'FINAL']),
    decision: z.enum(['APPROVED', 'REJECTED']), comment: z.string().trim().min(1).max(500) }).strict();
export const BonusRecordSchema = z.object({ packageId: z.uuid(),
    status: z.enum(['PROPOSED', 'ADMIN_APPROVED', 'APPROVED', 'REJECTED']),
    totalAmount: PackageOperationAmountSchema, companyRemainder: PackageOperationAmountSchema,
    ownerId: z.uuid(), adminReviewerId: z.uuid(), finalReviewerId: z.uuid(), proposedAt: z.iso.datetime(),
    allocations: z.array(z.object({ personId: z.uuid(), amount: PackageOperationAmountSchema }).strict()),
    reviews: z.array(z.object({ stage: z.enum(['ADMIN', 'FINAL']), decision: z.enum(['APPROVED', 'REJECTED']),
        reviewerId: z.uuid(), comment: z.string(), reviewedAt: z.iso.datetime() }).strict()),
    replayed: z.boolean().optional() }).strict();
export const PackageOperationsDashboardSchema = z.object({ publications: z.array(PublicationRecordSchema),
    workOrders: z.array(FlexibleWorkOrderSchema), expenses: z.array(ExpenseRecordSchema),
    closure: PackageClosureSchema.nullable(), closurePreview: ClosurePreviewSchema,
    bonus: BonusRecordSchema.nullable(), contents: z.array(z.object({ contentId: z.uuid(), contentCode: z.string(),
        status: z.string() }).strict()), people: z.array(z.object({ personId: z.uuid(), displayName: z.string(),
        roles: z.array(z.string()), payeeId: z.uuid().nullable() }).strict()) }).strict();
// BUG-0172（P0）：执行人包投影只保留标识与筛选字段；模板名、周期与本人任务数不下发。
export const MyVideoPackageListSchema = z.object({ items: z.array(z.object({
        projectId: z.uuid(), packageId: z.uuid(), packageCode: z.string(), packageTitle: z.string().nullable(),
        contentType: StandardContentTypeSchema.optional(),
        status: ServicePackageStatusSchema,
    }).strict()) }).strict();
/** BUG-0172：内容页给没有「服务包」页权限的成员（执行人、策划）用的最小包标识，
 * 只含 packageId / packageCode / packageTitle / contentType / status，不含容量、档位、周期、预算与费率。 */
export const ProjectPackageTierRefSchema = z.object({ code: z.string().min(1), name: z.string().min(1),
    count: z.number().int().nonnegative(), used: z.number().int().nonnegative(),
    remaining: z.number().int().nonnegative() }).strict();
export const ProjectPackageRefSchema = z.object({ packageId: z.uuid(), packageCode: z.string().min(1),
    packageTitle: z.string().nullable(), contentType: StandardContentTypeSchema,
    status: ServicePackageStatusSchema,
    // 只有内容创建/维护投影（策划、混合账号）才带容量与档位数量；纯执行人响应里不出现这两个键。
    capacity: z.number().int().nonnegative().optional(),
    tiers: z.array(ProjectPackageTierRefSchema).optional() }).strict();
export const ProjectPackageRefListSchema = z.object({ items: z.array(ProjectPackageRefSchema) }).strict();
export const VideoExecutorOptionsSchema = z.object({ items: z.array(z.object({ personId: z.uuid(),
        label: z.string() }).strict()), tasks: z.array(z.object({ code: z.string(), name: z.string() }).strict()) }).strict();
export const VideoPackageSummarySchema = z.object({
    packageId: z.uuid(), projectId: z.uuid(), packageCode: z.string(), status: ServicePackageStatusSchema,
    // BUG-0176：非负责人读取时金额被服务端裁剪为 0，用该标记让页面显示「无查看权限」而不是误导性的 ¥0.00。
    amountsHidden: z.boolean().optional(),
    version: z.number().int().positive(), contentType: StandardContentTypeSchema,
    serviceTemplateVersionId: z.uuid(), templateName: z.string().min(1), templateVersionName: z.string().min(1),
    deliverable: z.string().min(1), packageTitle: z.string().nullable(), businessOn: z.iso.date(),
    periodFrom: z.iso.date(), periodUntil: z.iso.date(), capacity: z.number().int().positive(),
    tiers: z.array(z.object({ code: z.string(), name: z.string(), description: z.string(),
        count: z.number().int().nonnegative(), used: z.number().int().nonnegative(),
        created: z.number().int().nonnegative(), delivered: z.number().int().nonnegative(),
        productionUnitPrice: ServicePackageAmountSchema,
        publicationUnitPrice: ServicePackageAmountSchema,
        unitPrice: ServicePackageAmountSchema,
        tasks: z.array(z.object({ code: z.string(), name: z.string(), unit: z.string(),
            quantity: z.number().int().positive(), rate: ServicePackageAmountSchema,
            subtotal: ServicePackageAmountSchema, isPublication: z.boolean() }).strict()),
    }).strict()),
    productionBudget: ServicePackageAmountSchema, publicationBudget: ServicePackageAmountSchema,
    budget: ServicePackageAmountSchema, flexibleReserve: ServicePackageAmountSchema,
    fixedCommitment: ServicePackageAmountSchema,
    managerId: z.uuid(), createdBy: z.uuid(), submittedBy: z.uuid().nullable(), confirmerId: z.uuid(),
    submittedAt: z.iso.datetime().nullable(), returnedAt: z.iso.datetime().nullable(),
    returnReason: z.string().nullable(), confirmedBy: z.uuid().nullable(), confirmedAt: z.iso.datetime().nullable(),
    startedBy: z.uuid().nullable(), startedAt: z.iso.datetime().nullable(), completedBy: z.uuid().nullable(),
    completedAt: z.iso.datetime().nullable(), completedOn: z.iso.date().nullable(), completionNote: z.string().nullable(),
    reopenedAt: z.iso.datetime().nullable(), reopenReason: z.string().nullable(),
    legacyTemplate: z.object({ id: z.uuid(), familyCode: z.string().nullable(),
        familyName: z.string().nullable(), versionNo: z.number().int().nullable(),
        versionName: z.string().nullable(), effectiveOn: z.iso.date().nullable() }).strict().nullable().optional(),
}).strict();
export const VideoPackageListResponseSchema = z.object({ items: z.array(VideoPackageSummarySchema) }).strict();
export const VideoPackageWriteResponseSchema = VideoPackageSummarySchema.extend({ replayed: z.boolean(),
    placeholdersCreated: z.number().int().nonnegative() }).strict();
export const VideoPackageOptionsSchema = z.object({
    templates: z.array(z.object({ versionId: z.uuid(), label: z.string(), effectiveOn: z.iso.date(),
        defaultCapacity: z.number().int().positive(), deliverable: z.string().min(1),
        variants: z.array(z.object({ variantKey: z.string(), name: z.string(), description: z.string(),
            groupKey: z.string().nullable().optional(), groupName: z.string().nullable().optional(),
            rateLabel: z.string().nullable().optional(),
            productionBudget: ServicePackageAmountSchema, publicationBudget: ServicePackageAmountSchema,
            totalBudget: ServicePackageAmountSchema }).strict()) }).strict()),
    managers: z.array(z.object({ personId: z.uuid(), label: z.string() }).strict()),
    confirmers: z.array(z.object({ personId: z.uuid(), label: z.string() }).strict()),
}).strict();
export const VideoPackageCompletionBlockerSchema = z.object({
    code: z.enum(['CONTENT_INCOMPLETE', 'TASK_INCOMPLETE', 'BLOCKING_ISSUE', 'PUBLICATION_INCOMPLETE',
        'CLAIM_PENDING', 'ADJUSTMENT_PENDING', 'FEE_PENDING', 'STATEMENT_UNPAID']),
    label: z.string().min(1), count: z.number().int().positive(), href: z.string().min(1),
}).strict();
export const VideoPackageCompletionWarningSchema = z.object({
    code: z.literal('UNUSED_CAPACITY'), label: z.string().min(1), count: z.number().int().positive(),
}).strict();
export const VideoPackageCompletionPreviewSchema = z.object({
    packageId: z.uuid(), status: ServicePackageStatusSchema, version: z.number().int().positive(),
    canComplete: z.boolean(), blockers: z.array(VideoPackageCompletionBlockerSchema),
    warnings: z.array(VideoPackageCompletionWarningSchema), usedCapacity: z.number().int().nonnegative(),
    capacity: z.number().int().positive(),
}).strict();
export const StandardDraftResponseSchema = z.object({ familyId: z.uuid(), familyCode: z.string(),
    versionId: z.uuid(), versionNo: z.number().int().positive(), version: z.number().int().positive(),
    kind: StandardKindSchema, contentType: StandardContentTypeSchema,
    status: z.literal('DRAFT'), effectiveOn: z.iso.date(), replayed: z.boolean(),
}).strict();
export const StandardWithdrawRequestSchema = z.object({ expectedVersion: z.number().int().positive(),
    reason: z.string().trim().min(1).max(500) }).strict();
export const StandardWithdrawResponseSchema = StandardDraftResponseSchema.omit({ status: true }).extend({
    status: z.literal('WITHDRAWN'), reason: z.string().min(1),
}).strict();
export const StandardPublishResponseSchema = StandardDraftResponseSchema.omit({ status: true }).extend({
    status: z.literal('PUBLISHED'), confirmedAt: z.iso.datetime(),
    impact: z.object({ existingInstancesChanged: z.literal(0), futureAmount: z.null() }).strict(),
}).strict();
export const StandardConfirmerListSchema = z.object({ items: z.array(z.object({
        personId: z.uuid(), displayName: z.string().min(1), code: z.string().min(1),
    }).strict()) }).strict();
export const StandardPreviewSchema = z.object({
    versionId: z.uuid(), comparedToVersionId: z.uuid().nullable(),
    changes: z.array(z.object({ field: z.string(), before: z.string().nullable(), after: z.string().nullable() }).strict()),
    impact: z.object({ standardVersionsPublished: z.literal(1), existingInstancesChanged: z.literal(0),
        futureAmount: z.null() }).strict(),
}).strict();
export const PackageSimulationRequestSchema = z.object({
    templateFamilyId: z.uuid(), rateFamilyId: z.uuid(), on: z.iso.date(),
    capacity: z.number().int().min(1).max(100000),
    tiers: z.array(z.object({ code: z.string().min(1), count: z.number().int().nonnegative() }).strict()).min(1),
    budget: z.string().regex(/^(?:0|[1-9]\d{0,15})\.\d{2}$/),
    flexibleReserve: z.string().regex(/^(?:0|[1-9]\d{0,15})\.\d{2}$/),
}).strict();
export const PackageSimulationResponseSchema = z.object({
    templateVersionId: z.uuid(), rateVersionId: z.uuid(), on: z.iso.date(),
    capacity: z.number().int().positive(),
    breakdown: z.array(z.object({ tierCode: z.string(), count: z.number().int().nonnegative(),
        tasks: z.array(z.object({ taskCode: z.string(), unitAmount: z.string(), commitment: z.string() }).strict()),
        commitment: z.string() }).strict()),
    fixedCommitment: z.string(), flexibleReserve: z.string(), budget: z.string(), requiredBudget: z.string(),
    available: z.string(), shortfall: z.string(), feasible: z.boolean(),
}).strict();
/**
 * BUG-0177：发布成片单文件上限的**唯一口径**。服务端解析层/路由校验与前端两处上传入口
 * 都必须引用这里，禁止再写字面量（曾出现抽屉 64MiB、建计划弹窗 300MB 两套上限，
 * 用户传 80MB 前端放行、服务端 413，违反「不得允许上传但失败」）。
 * BUG-0189 解冻后，成片上限调整为 200MiB；超过 minio-js 64MiB multipart
 * 切换点的对象由服务端预签名单次 PUT 上传，避开生产 COS multipart ETag 兼容问题。
 */
export const PUBLICATION_VIDEO_MAX_BYTES = 209_715_200;
/** 与上限配套的展示文案，统一写 200MiB（二进制单位）。 */
export const PUBLICATION_VIDEO_MAX_LABEL = '200MiB';
export const PublicationVideoUploadResponseSchema = z.object({
    stagingId: z.uuid(), projectId: z.uuid(), originalFilename: z.string().min(1),
    byteSize: z.number().int().min(1).max(PUBLICATION_VIDEO_MAX_BYTES),
    detectedMime: z.string().min(1), sha256: z.string().regex(/^[0-9a-f]{64}$/),
    expiresAt: z.iso.datetime(),
}).strict();
export const StageContractUploadResponseSchema = z.object({
    stagingId: z.uuid(), projectId: z.uuid(), originalFilename: z.string().min(1),
    byteSize: z.number().int().min(1).max(52_428_800),
    detectedMime: z.enum(['application/pdf', 'image/png', 'image/jpeg']),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    expiresAt: z.iso.datetime({ offset: true }), replayed: z.boolean(),
}).strict();
/** 财务域付款凭证：属于付款事实/结算单，不属于某个项目，因此没有 projectId。 */
export const PaymentEvidenceUploadResponseSchema = z.object({
    stagingId: z.uuid(), originalFilename: z.string().min(1),
    byteSize: z.number().int().min(1).max(52_428_800),
    detectedMime: z.enum(['application/pdf', 'image/png', 'image/jpeg']),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    expiresAt: z.iso.datetime({ offset: true }), replayed: z.boolean(),
}).strict();
export const ContractAttachmentDownloadTicketSchema = z.object({
    ticket: z.string().min(1).max(1024), expiresAt: z.iso.datetime({ offset: true }),
}).strict();
export const ArchiveContractResponseSchema = z.object({
    contractId: z.uuid(), contractCode: z.string().regex(/^CON-\d{4}-\d{6}$/),
    projectId: z.uuid(), projectVersion: z.number().int().min(2),
    attachmentIds: z.array(z.uuid()).min(1), replayed: z.boolean(),
}).strict();
export const VoidContractRequestSchema = z.object({
    expectedVersion: z.number().int().min(1),
    reason: z.string().trim().min(1).max(500),
}).strict();
export const VoidContractResponseSchema = z.object({
    contractId: z.uuid(), projectId: z.uuid(), projectVersion: z.number().int().min(2),
    status: z.literal('VOID'), voidedAt: z.iso.datetime({ offset: true }), replayed: z.boolean(),
}).strict();
export const UnlinkContractAttachmentRequestSchema = VoidContractRequestSchema;
export const UnlinkContractAttachmentResponseSchema = z.object({
    projectId: z.uuid(), contractId: z.uuid(), attachmentId: z.uuid(),
    projectVersion: z.number().int().min(2), unlinkedAt: z.iso.datetime({ offset: true }),
    retention: z.literal('ON_HOLD'), replayed: z.boolean(),
}).strict();
export const ReleaseContractAttachmentRetentionRequestSchema = VoidContractRequestSchema;
export const ReleaseContractAttachmentRetentionResponseSchema = z.object({
    projectId: z.uuid(), contractId: z.uuid(), attachmentId: z.uuid(),
    projectVersion: z.number().int().min(2),
    retentionReleasedAt: z.iso.datetime({ offset: true }), retainUntil: z.iso.datetime({ offset: true }),
    retention: z.literal('RELEASED'), policyVersion: z.literal('1.0'),
    policySha256: z.literal('30fa939749fc870181da81cc42a67f8c07cb9eb466a16369836bf57b17f245ef'),
    replayed: z.boolean(),
}).strict();
export const ContractListResponseSchema = z.object({
    items: z.array(z.object({
        contractId: z.uuid(), projectId: z.uuid(), code: z.string().regex(/^CON-\d{4}-\d{6}$/),
        name: z.string().min(1), kind: z.enum(['ORIGINAL', 'SUPPLEMENT', 'RENEWAL', 'CORRECTION']),
        parentContractId: z.uuid().nullable(), counterpartyId: z.uuid(),
        amountKind: z.enum(['FIXED', 'OPEN']), amount: z.string().nullable(),
        signedOn: z.string().nullable(), validFrom: z.string().nullable(), validUntil: z.string().nullable(),
        status: z.enum(['ARCHIVED', 'VOID']), voidedAt: z.iso.datetime({ offset: true }).nullable(),
        voidReason: z.string().nullable(), notes: z.string().nullable(),
        attachmentMetadata: z.array(z.object({
            attachmentId: z.uuid(), filename: z.string(), byteSize: z.number().int().positive(),
            detectedMime: z.string(), sha256: z.string().regex(/^[0-9a-f]{64}$/),
            unlinkedAt: z.iso.datetime({ offset: true }).nullable(), unlinkReason: z.string().nullable(),
            legalHold: z.boolean(), retainUntil: z.iso.datetime({ offset: true }).nullable(),
            retentionReleasedAt: z.iso.datetime({ offset: true }).nullable(),
            retentionReleaseReason: z.string().nullable(), retentionPolicySha256: z.string().regex(/^[0-9a-f]{64}$/).nullable(),
            reclaimedAt: z.iso.datetime({ offset: true }).nullable(),
        }).strict()),
    }).strict()),
    nextCursor: z.string().regex(/^CON-\d{4}-\d{6}$/).nullable(),
}).strict();
// BUG-0169：内容级共享附件（文件 / 外部链接），绑定 contentId，不绑 taskId。
// 版本历史只追加不物理删除；链接只接受 http/https。
export const ContentSharedAttachmentKindSchema = z.enum(['FILE', 'LINK']);
export const ContentSharedAttachmentActionSchema = z.enum(['CREATE', 'REPLACE', 'RETITLE', 'RETRACT']);
export const ContentSharedAttachmentStatusSchema = z.enum(['ACTIVE', 'RETRACTED']);
const SharedAttachmentTitleSchema = z.string().trim().min(1).max(160);
const SharedAttachmentUrlSchema = z.string().trim().min(1).max(2048)
    .refine((value) => /^https?:\/\//iu.test(value), '链接只接受 http/https');
export const ContentSharedAttachmentVersionSchema = z.object({
    versionNo: z.number().int().positive(),
    action: ContentSharedAttachmentActionSchema,
    kind: ContentSharedAttachmentKindSchema,
    title: z.string().min(1).max(160),
    fileName: z.string().min(1).max(255).nullable(),
    mimeType: z.string().min(1).max(120).nullable(),
    byteSize: z.number().int().nonnegative().nullable(),
    url: z.string().max(2048).nullable(),
    createdBy: z.uuid(),
    createdByName: z.string().min(1).max(120),
    createdAt: z.iso.datetime({ offset: true }),
}).strict();
export const ContentSharedAttachmentSchema = z.object({
    attachmentId: z.uuid(),
    contentId: z.uuid(),
    kind: ContentSharedAttachmentKindSchema,
    title: z.string().min(1).max(160),
    status: ContentSharedAttachmentStatusSchema,
    version: z.number().int().positive(),
    current: ContentSharedAttachmentVersionSchema,
    versions: z.array(ContentSharedAttachmentVersionSchema),
    createdBy: z.uuid(),
    createdAt: z.iso.datetime({ offset: true }),
    updatedAt: z.iso.datetime({ offset: true }),
    retractedAt: z.iso.datetime({ offset: true }).nullable(),
    canManage: z.boolean(),
}).strict();
export const ContentSharedAttachmentListSchema = z.object({
    contentId: z.uuid(),
    items: z.array(ContentSharedAttachmentSchema),
    canManage: z.boolean(),
}).strict();
export const CreateContentSharedAttachmentRequestSchema = z.object({
    kind: ContentSharedAttachmentKindSchema,
    title: SharedAttachmentTitleSchema,
    stagingId: z.uuid().nullable().optional(),
    url: z.string().trim().max(2048).nullable().optional(),
}).strict().refine((value) => value.kind === 'FILE'
    ? typeof value.stagingId === 'string' && (value.url === undefined || value.url === null)
    : typeof value.url === 'string' && value.url.length > 0 && (value.stagingId === undefined || value.stagingId === null), { message: '文件附件需要 stagingId，链接附件需要 url' })
    .refine((value) => value.kind !== 'LINK' || (typeof value.url === 'string' && /^https?:\/\//iu.test(value.url)), { message: '链接只接受 http/https', path: ['url'] });
export const UpdateContentSharedAttachmentRequestSchema = z.object({
    expectedVersion: z.number().int().positive(),
    title: SharedAttachmentTitleSchema.optional(),
    stagingId: z.uuid().nullable().optional(),
    url: SharedAttachmentUrlSchema.nullable().optional(),
}).strict();
export const RetractContentSharedAttachmentRequestSchema = z.object({
    expectedVersion: z.number().int().positive(),
    reason: z.string().trim().min(2).max(500).nullable().optional(),
}).strict();
export const ContentSharedAttachmentWriteResponseSchema = ContentSharedAttachmentSchema.extend({
    replayed: z.boolean(),
}).strict();
// 上传暂存回执（复用既有暂存结构，仅含扫描结果与对象标识）。
export const ContentSharedAttachmentStageSchema = z.object({
    stagingId: z.uuid(),
    projectId: z.uuid(),
    originalFilename: z.string().min(1).max(255),
    byteSize: z.number().int().positive(),
    detectedMime: z.string().min(1).max(120),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    expiresAt: z.iso.datetime({ offset: true }),
    replayed: z.boolean(),
}).strict();
// MCP 个人访问令牌：页面自助签发/查看/吊销。
// 明文只在签发响应中出现一次；列表只返回前缀和状态。
export const mcpToolNames = [
    'get_my_workbench',
    'list_my_tasks',
    'list_my_projects',
    'get_project',
    'get_my_settlement',
    'get_settlement_preview',
    'get_project_cost_summary',
    // MCP-REQ-2026-001（161）只读第一阶段：项目 → 服务包 → 内容 → 内容任务。
    'list_project_packages',
    'list_project_contents',
    'get_content',
    'get_project_content_progress',
];
export const McpToolNameSchema = z.enum(mcpToolNames);
export const McpToolDefinitionSchema = z.object({
    name: McpToolNameSchema,
    label: z.string().min(1).max(80),
    description: z.string().min(1).max(300),
    requiredCapability: CapabilityCodeSchema,
    requiredPage: PageKeySchema,
    dataScope: z.enum(['SELF', 'PROJECT', 'GLOBAL']),
    sensitivity: z.enum(['STANDARD', 'SENSITIVE']),
}).strict();
// MCP-REQ-2026-001（161）：项目内容只读查询契约。
// 口径（2026-10-28 定稿）：已创建内容 = 除 VOID 外全部内容（含 PLACEHOLDER 占位）；
// 一律不返回金额、分成、收款人、附件原文、平台凭证、交付/发布摘要与 published。
export const McpReadContentStatusSchema = z.enum([
    'PLACEHOLDER', 'PLANNED', 'TIERED', 'IN_PROGRESS', 'PENDING_DELIVERY', 'DELIVERED',
]);
export const McpProjectPackageSchema = z.object({
    packageId: z.uuid(),
    packageCode: z.string().min(1),
    packageTitle: z.string().nullable(),
    contentType: StandardContentTypeSchema,
    status: ServicePackageStatusSchema,
    capacity: z.number().int().nonnegative(),
    tieredCount: z.number().int().nonnegative(),
    usedCapacity: z.number().int().nonnegative(),
    placeholderCount: z.number().int().nonnegative(),
    contentCount: z.number().int().nonnegative(),
    latestContentUpdatedAt: z.iso.datetime({ offset: true }).nullable(),
    webPath: z.string().min(1),
}).strict();
export const McpProjectPackageListSchema = z.object({
    projectId: z.uuid(),
    items: z.array(McpProjectPackageSchema),
    nextAfter: z.number().int().nonnegative().nullable(),
    total: z.number().int().nonnegative(),
}).strict();
export const McpProjectContentSchema = z.object({
    contentId: z.uuid(),
    contentCode: z.string().min(1),
    legacyNo: z.string().max(40).nullable(),
    displayNo: ContentDisplayNoSchema.nullable().default(null),
    title: z.string().nullable(),
    contentType: StandardContentTypeSchema,
    status: McpReadContentStatusSchema,
    packageId: z.uuid(),
    packageCode: z.string().min(1),
    ordinal: z.number().int().positive(),
    updatedAt: z.iso.datetime({ offset: true }),
    webPath: z.string().min(1),
}).strict();
export const McpProjectContentListSchema = z.object({
    projectId: z.uuid(),
    items: z.array(McpProjectContentSchema),
    nextAfter: z.number().int().nonnegative().nullable(),
    total: z.number().int().nonnegative(),
}).strict();
export const McpContentTaskSchema = z.object({
    taskId: z.uuid(),
    taskCode: z.string().min(1),
    taskName: z.string().min(1),
    status: ProductionTaskStatusSchema,
    assignees: z.array(z.object({
        displayName: z.string().min(1),
        role: z.enum(['PRIMARY', 'COLLABORATOR']),
        assignedAt: z.iso.datetime({ offset: true }),
    }).strict()),
}).strict();
export const McpContentDetailSchema = z.object({
    contentId: z.uuid(),
    contentCode: z.string().min(1),
    legacyNo: z.string().max(40).nullable(),
    displayNo: ContentDisplayNoSchema.nullable().default(null),
    title: z.string().nullable(),
    contentType: StandardContentTypeSchema,
    status: McpReadContentStatusSchema,
    packageId: z.uuid(),
    packageCode: z.string().min(1),
    tasks: z.array(McpContentTaskSchema),
    webPath: z.string().min(1),
}).strict();
export const McpProjectContentProgressPackageSchema = z.object({
    packageId: z.uuid(),
    packageCode: z.string().min(1),
    contentType: StandardContentTypeSchema,
    status: ServicePackageStatusSchema,
    capacity: z.number().int().nonnegative(),
    tieredCount: z.number().int().nonnegative(),
    usedCapacity: z.number().int().nonnegative(),
    placeholder: z.number().int().nonnegative(),
    planned: z.number().int().nonnegative(),
    tiered: z.number().int().nonnegative(),
    inProgress: z.number().int().nonnegative(),
    pendingDelivery: z.number().int().nonnegative(),
    delivered: z.number().int().nonnegative(),
    contentCount: z.number().int().nonnegative(),
}).strict();
export const McpProjectContentProgressTotalsSchema = z.object({
    capacity: z.number().int().nonnegative(),
    tieredCount: z.number().int().nonnegative(),
    usedCapacity: z.number().int().nonnegative(),
    placeholder: z.number().int().nonnegative(),
    planned: z.number().int().nonnegative(),
    tiered: z.number().int().nonnegative(),
    inProgress: z.number().int().nonnegative(),
    pendingDelivery: z.number().int().nonnegative(),
    delivered: z.number().int().nonnegative(),
    contentCount: z.number().int().nonnegative(),
}).strict();
export const McpProjectContentProgressSchema = z.object({
    projectId: z.uuid(),
    packages: z.array(McpProjectContentProgressPackageSchema),
    totals: McpProjectContentProgressTotalsSchema,
}).strict();
export const McpAccessGrantSchema = z.object({
    accountId: z.uuid(),
    enabled: z.boolean(),
    allowedTools: z.array(McpToolNameSchema),
    expiresAt: z.iso.datetime({ offset: true }).nullable(),
    updatedAt: z.iso.datetime({ offset: true }),
    version: z.number().int().positive(),
}).strict();
export const McpAccessAccountSchema = z.object({
    accountId: z.uuid(),
    personCode: z.string().min(1).max(120),
    displayName: z.string().min(1).max(120),
    username: z.string().regex(/^[a-z0-9][a-z0-9._-]{2,63}$/),
    accountStatus: AccountStatusSchema,
    identityLinked: z.boolean(),
    permissionTools: z.array(McpToolNameSchema),
    effectiveTools: z.array(McpToolNameSchema),
    grant: McpAccessGrantSchema.nullable(),
}).strict();
export const McpAccessAccountListSchema = z.object({
    items: z.array(McpAccessAccountSchema),
    tools: z.array(McpToolDefinitionSchema),
}).strict();
export const ChangeMcpAccessRequestSchema = z.object({
    expectedVersion: z.number().int().nonnegative(),
    enabled: z.boolean(),
    allowedTools: z.array(McpToolNameSchema),
    expiresAt: z.iso.datetime({ offset: true }).nullable(),
    reason: z.string().trim().min(2).max(500),
}).strict().superRefine((value, context) => {
    if (value.enabled && value.allowedTools.length === 0) {
        context.addIssue({ code: 'custom', path: ['allowedTools'], message: '启用 MCP 时至少选择一个工具' });
    }
    if (new Set(value.allowedTools).size !== value.allowedTools.length) {
        context.addIssue({ code: 'custom', path: ['allowedTools'], message: 'MCP 工具不能重复' });
    }
});
export const ChangeMcpAccessResponseSchema = z.object({
    grant: McpAccessGrantSchema,
    revokedTokenCount: z.number().int().nonnegative(),
    replayed: z.boolean(),
}).strict();
export const McpTokenSummarySchema = z.object({
    tokenId: z.uuid(),
    label: z.string().min(1).max(120),
    tokenPrefix: z.string().min(1).max(32),
    scopes: z.array(z.string().min(1)).min(1),
    createdAt: z.iso.datetime({ offset: true }),
    expiresAt: z.iso.datetime({ offset: true }),
    revokedAt: z.iso.datetime({ offset: true }).nullable(),
    lastUsedAt: z.iso.datetime({ offset: true }).nullable(),
}).strict();
export const McpTokenListSchema = z.object({
    items: z.array(McpTokenSummarySchema),
}).strict();
export const IssueMcpTokenRequestSchema = z.object({
    label: z.string().trim().min(1).max(120),
    days: z.number().int().min(1).max(90),
}).strict();
export const IssueMcpTokenResponseSchema = z.object({
    tokenId: z.uuid(),
    token: z.string().min(20),
    tokenPrefix: z.string().min(1).max(32),
    label: z.string().min(1).max(120),
    expiresAt: z.iso.datetime({ offset: true }),
}).strict();
export const RevokeMcpTokenResponseSchema = z.object({
    tokenId: z.uuid(),
    revokedAt: z.iso.datetime({ offset: true }),
}).strict();
export const ErrorCodeSchema = z.enum([
    'UNAUTHENTICATED',
    'FORBIDDEN',
    'STATE_CONFLICT',
    'VERSION_CONFLICT',
    'QUOTA_EXHAUSTED',
    'BUDGET_INSUFFICIENT',
    'RATE_MISSING',
    'PERCENT_NOT_100',
    'DUPLICATE_REQUEST',
    'IDEMPOTENCY_PAYLOAD_CONFLICT',
    'IDEMPOTENCY_KEY_REQUIRED',
    'IDEMPOTENCY_KEY_INVALID',
    'IDENTITY_DEPENDENCY_UNAVAILABLE',
    'PAYEE_QUERY_UNAVAILABLE',
    'PAYEE_NOT_CONFIGURED',
    'PROJECT_COMMAND_UNAVAILABLE',
    'PROJECT_QUERY_UNAVAILABLE',
    'PROJECT_QUERY_INVALID',
    'PROJECT_NOT_FOUND',
    'CUSTOMER_NOT_FOUND',
    'CUSTOMER_QUERY_INVALID',
    'CUSTOMER_COMMAND_INVALID',
    'CUSTOMER_COMMAND_UNAVAILABLE',
    'CUSTOMER_QUERY_UNAVAILABLE',
    'REFERENCE_NOT_FOUND',
    'EVIDENCE_REQUIRED',
    'PLANNED_AT_REQUIRED',
    'PUBLISH_DATE_OUT_OF_RANGE',
    'PUBLICATION_RESULT_INVALID',
    'PUBLICATION_EVIDENCE_INVALID',
    'CONTENT_SHARED_ATTACHMENT_NOT_FOUND',
    'CONTENT_SHARED_ATTACHMENT_INVALID',
    'CONTENT_SHARED_ATTACHMENT_LINK_INVALID',
    'MCP_TOKEN_INVALID',
    'MCP_TOKEN_LIMIT_REACHED',
    'MCP_TOKEN_NOT_FOUND',
    'MCP_TOKEN_UNAVAILABLE',
    'MCP_ACCESS_DENIED',
    'MCP_ACCESS_NOT_FOUND',
    'MCP_ACCESS_INVALID',
    'MCP_ACCESS_UNAVAILABLE',
    'MCP_RATE_LIMITED',
]);
//# sourceMappingURL=index.js.map